import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaShoppingBag, FaMapMarkerAlt, FaCalendarAlt, FaDollarSign, FaBox, FaChevronDown, FaChevronUp, FaUpload, FaFileUpload, FaSignOutAlt, FaEye, FaEyeSlash, FaUser, FaEnvelope, FaLock, FaKey, FaInfoCircle } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone } from "@fortawesome/free-solid-svg-icons";
import base_url from "../../api";
import "./Profile.css";

// Utility function to format date as "15 Jan 2025"
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "N/A";
  
  const day = date.getDate();
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  
  return `${day} ${month} ${year}`;
};

const Profile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [measurementOrders, setMeasurementOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingPaymentProof, setUploadingPaymentProof] = useState({});
  const [activeTab, setActiveTab] = useState("account");
  const [expandedMeasurementOrders, setExpandedMeasurementOrders] = useState(new Set()); // Track which measurement orders are expanded
  const [expandedOrders, setExpandedOrders] = useState(new Set()); // Track which regular orders are expanded
  const [expandedRecipientMeasurements, setExpandedRecipientMeasurements] = useState(new Set()); // Track which recipient measurements are expanded

  // Password update states
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [updating, setUpdating] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});
  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Phone update states
  const [newPhone, setNewPhone] = useState("");
  const [updatingPhone, setUpdatingPhone] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  // Billing information states for measurement orders
  const [billingInfo, setBillingInfo] = useState({});
  const [billingErrors, setBillingErrors] = useState({}); // keyed by orderNo, then field name
  const [confirmingOrder, setConfirmingOrder] = useState({});
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState({}); // keyed by orderNo
  const [cities, setCities] = useState({}); // keyed by orderNo
  const [selectedCountry, setSelectedCountry] = useState({}); // keyed by orderNo, default to "Canada"

const [sendingMap, setSendingMap] = useState({});
  const [editingRecipient, setEditingRecipient] = useState(null); // { orderNo, lookId, recipientIndex, field: 'email' | 'name' }
  const [editingValue, setEditingValue] = useState("");
  const [addingRecipient, setAddingRecipient] = useState(null); // { orderNo, lookId }
  const [newRecipient, setNewRecipient] = useState({ name: "", email: "" });
  const [updatingRecipients, setUpdatingRecipients] = useState({});
  const [orderLockStatus, setOrderLockStatus] = useState({}); // { orderNo: { isLocked, status } }
  const [fetchingLockStatus, setFetchingLockStatus] = useState(new Set()); // Track which orders are currently being fetched
  const [fetchingOrderDetails, setFetchingOrderDetails] = useState(new Set()); // Track which orders are currently fetching details

  // Shawl order address editing states
  const [shawlOrderAddress, setShawlOrderAddress] = useState({}); // keyed by orderId
  const [updatingShawlAddress, setUpdatingShawlAddress] = useState({}); // keyed by orderId
  const [showShawlPaymentReviewPopup, setShowShawlPaymentReviewPopup] = useState(false);

  const sendReminder = async (orderNo, recipientEmail) => {
    if (!orderNo || !recipientEmail) return;

    setSendingMap(prev => ({
      ...prev,
      [recipientEmail]: true,
    }));

    try {
      const res = await fetch(`${base_url}/send-reminder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderNo, recipientEmail }),
      });

      const data = await res.json();
      alert(data.message || "Reminder sent successfully");
    } catch (e) {
      alert("Failed to send reminder");
    } finally {
      setSendingMap(prev => ({
        ...prev,
        [recipientEmail]: false,
      }));
    }
  };

  // Email validation function
  const validateEmail = (email) => {
    if (!email || !email.trim()) {
      return "Email is required";
    }
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    const trimmedEmail = email.trim();
    if (!emailRegex.test(trimmedEmail)) {
      return "Please enter a valid email address";
    }
    if (trimmedEmail.startsWith('.') || trimmedEmail.endsWith('.') || 
        trimmedEmail.includes('..') || 
        !trimmedEmail.includes('@') ||
        trimmedEmail.split('@')[1]?.split('.').length < 2) {
      return "Please enter a valid email address";
    }
    return null;
  };

  // Fetch order lock status
  const fetchOrderLockStatus = async (orderNo, forceRefresh = false) => {
    // Prevent duplicate fetches
    if (fetchingLockStatus.has(orderNo) && !forceRefresh) {
      return null;
    }
    
    // Check if we already have the status (only if not forcing refresh)
    if (!forceRefresh) {
      const existingStatus = orderLockStatus[orderNo];
      if (existingStatus && (existingStatus.status || existingStatus.isLocked !== undefined)) {
        return existingStatus;
      }
    }
    
    // Mark as fetching
    setFetchingLockStatus(prev => new Set(prev).add(orderNo));
    
    try {
      const res = await fetch(`${base_url}/orders/${orderNo}/status`);
      const data = await res.json();
      
      if (res.ok && data.success) {
        const statusData = {
          isLocked: data.isLocked,
          status: data.status,
          order: data.order // Store order object to check if it exists in orders table
        };
        setOrderLockStatus(prev => ({
          ...prev,
          [orderNo]: statusData
        }));
        return statusData;
      }
    } catch (err) {
      console.error("Error fetching order lock status:", err);
    } finally {
      // Remove from fetching set
      setFetchingLockStatus(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderNo);
        return newSet;
      });
    }
    return null;
  };

  // Update recipient email or name
  const handleUpdateRecipient = async (orderNo, lookId, recipientIndex, field, newValue) => {
    if (!newValue || !newValue.trim()) {
      toast.error(`${field === 'email' ? 'Email' : 'Name'} cannot be empty`);
      return;
    }

    if (field === 'email') {
      const emailError = validateEmail(newValue);
      if (emailError) {
        toast.error(emailError);
        return;
      }
    }

    // Check if order is locked (locked orders cannot be edited in any tab)
    const lockStatus = orderLockStatus[orderNo];
    if (lockStatus?.isLocked) {
      toast.error("Order is locked. Cannot edit recipients.");
      return;
    }
    
    // Note: isConfirmed check is handled at UI level in Orders tab only
    // Measurements tab allows editing even after confirmation

    // Get recipient ID from the order data
    const order = measurementOrders.find(o => o.orderNo === orderNo);
    if (!order || !order.looks) {
      toast.error("Order not found");
      return;
    }

    const look = order.looks.find(l => (l.lookId === lookId || l.id === lookId));
    if (!look || !look.recipients || !look.recipients[recipientIndex]) {
      toast.error("Recipient not found");
      return;
    }

    const recipientId = look.recipients[recipientIndex].recipientId || look.recipients[recipientIndex].id;
    if (!recipientId) {
      toast.error("Recipient ID not found. Please refresh the page.");
      return;
    }

    const key = `${orderNo}-${lookId}-${recipientIndex}`;
    setUpdatingRecipients(prev => ({ ...prev, [key]: true }));

    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`${base_url}/orders/${orderNo}/recipients/${recipientId}`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          field,
          value: newValue.trim(),
          userEmail: user?.email
        }),
      });

      const data = await res.json();
      
      if (res.ok && data.success) {
        // Refresh measurement orders to get updated data
        if (loggedInUser && loggedInUser.email) {
          fetchAdminOrders(loggedInUser.email);
        }
        
        toast.success(`${field === 'email' ? 'Email' : 'Name'} updated successfully`);
        setEditingRecipient(null);
        setEditingValue("");
      } else {
        toast.error(data.error || "Failed to update recipient");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error updating recipient. Please try again.");
    } finally {
      setUpdatingRecipients(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    }
  };

  // Delete recipient
  const handleDeleteRecipient = async (orderNo, lookId, recipientIndex) => {
    if (!window.confirm("Are you sure you want to remove this recipient? This action cannot be undone.")) {
      return;
    }

    // Check if order is locked (locked orders cannot be edited in any tab)
    const lockStatus = orderLockStatus[orderNo];
    if (lockStatus?.isLocked) {
      toast.error("Order is locked. Cannot remove recipients.");
      return;
    }
    
    // Note: isConfirmed check is handled at UI level in Orders tab only
    // Measurements tab allows editing even after confirmation

    // Get recipient ID from the order data
    const order = measurementOrders.find(o => o.orderNo === orderNo);
    if (!order || !order.looks) {
      toast.error("Order not found");
      return;
    }

    const look = order.looks.find(l => (l.lookId === lookId || l.id === lookId));
    if (!look || !look.recipients || !look.recipients[recipientIndex]) {
      toast.error("Recipient not found");
      return;
    }

    const recipientId = look.recipients[recipientIndex].recipientId || look.recipients[recipientIndex].id;
    if (!recipientId) {
      toast.error("Recipient ID not found. Please refresh the page.");
      return;
    }

    const key = `${orderNo}-${lookId}-${recipientIndex}`;
    setUpdatingRecipients(prev => ({ ...prev, [key]: true }));

    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`${base_url}/orders/${orderNo}/recipients/${recipientId}`, {
        method: "DELETE",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          userEmail: user?.email,
          sendNotificationEmails: true
        }),
      });

      const data = await res.json();
      
      if (res.ok && data.success) {
        // Optimistically update total quantity immediately (subtract 1)
        setMeasurementOrders(prevOrders => {
          return prevOrders.map(order => {
            if (order.orderNo === orderNo || String(order.orderNo) === String(orderNo)) {
              const currentTotal = order.totalQuantity || 0;
              return {
                ...order,
                totalQuantity: Math.max(0, currentTotal - 1) // Ensure it doesn't go below 0
              };
            }
            return order;
          });
        });
        
        // Refresh measurement orders to get updated data from backend
        if (loggedInUser && loggedInUser.email) {
          fetchAdminOrders(loggedInUser.email).then(() => {
            // Recalculate total quantity based on actual recipients after data is refreshed
            setTimeout(() => {
              updateOrderTotalQuantity(orderNo);
            }, 200);
          });
        } else {
          // If no logged in user, still update the quantity after a delay
          setTimeout(() => {
            updateOrderTotalQuantity(orderNo);
          }, 200);
        }
        
        toast.success("Recipient removed successfully");
      } else {
        toast.error(data.error || "Failed to remove recipient");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error removing recipient. Please try again.");
    } finally {
      setUpdatingRecipients(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    }
  };

  // Add new recipient
  const handleAddRecipient = async (orderNo, lookId) => {
    if (!newRecipient.name || !newRecipient.name.trim()) {
      toast.error("Recipient name is required");
      return;
    }

    const emailError = validateEmail(newRecipient.email);
    if (emailError) {
      toast.error(emailError);
      return;
    }

    // Check if order is locked (locked orders cannot be edited in any tab)
    const lockStatus = orderLockStatus[orderNo];
    if (lockStatus?.isLocked) {
      toast.error("Order is locked. Cannot add recipients.");
      return;
    }
    
    // Note: isConfirmed check is handled at UI level in Orders tab only
    // Measurements tab allows editing even after confirmation

    const key = `${orderNo}-${lookId}-add`;
    setUpdatingRecipients(prev => ({ ...prev, [key]: true }));

    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`${base_url}/orders/${orderNo}/recipients`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          lookId,
          name: newRecipient.name.trim(),
          email: newRecipient.email.trim(),
          userEmail: user?.email
        }),
      });

      const data = await res.json();
      
      if (res.ok && data.success) {
        // Optimistically update total quantity immediately (add 1)
        setMeasurementOrders(prevOrders => {
          return prevOrders.map(order => {
            if (order.orderNo === orderNo || String(order.orderNo) === String(orderNo)) {
              const currentTotal = order.totalQuantity || 0;
              return {
                ...order,
                totalQuantity: currentTotal + 1
              };
            }
            return order;
          });
        });
        
        // Refresh measurement orders to get updated data from backend
        if (loggedInUser && loggedInUser.email) {
          fetchAdminOrders(loggedInUser.email).then(() => {
            // Recalculate total quantity based on actual recipients after data is refreshed
            setTimeout(() => {
              updateOrderTotalQuantity(orderNo);
            }, 200);
          });
        } else {
          // If no logged in user, still update the quantity after a delay
          setTimeout(() => {
            updateOrderTotalQuantity(orderNo);
          }, 200);
        }
        
        toast.success("Recipient added successfully. Measurement invite will be sent.");
        setAddingRecipient(null);
        setNewRecipient({ name: "", email: "" });
      } else {
        toast.error(data.error || "Failed to add recipient");
      }
    } catch (err) {
      console.error(err);
      toast.error("Error adding recipient. Please try again.");
    } finally {
      setUpdatingRecipients(prev => {
        const updated = { ...prev };
        delete updated[key];
        return updated;
      });
    }
  };

  // Comprehensive Currency and tax configuration for all countries
  // Note: exchangeRate is now fetched live from API, only code, symbol, and taxRate are stored here
  const CURRENCY_CONFIG = {
    "United States": {
      code: "USD",
      symbol: "$",
      taxRate: 0.08, // 8% sales tax (varies by state)
    },
    "Canada": {
      code: "CAD",
      symbol: "C$",
      taxRate: 0.13, // 13% HST/GST
    },
    "United Kingdom": {
      code: "GBP",
      symbol: "£",
      taxRate: 0.20, // 20% VAT
    },
    "Australia": {
      code: "AUD",
      symbol: "A$",
      taxRate: 0.10, // 10% GST
    },
    "Germany": {
      code: "EUR",
      symbol: "€",
      taxRate: 0.19, // 19% VAT
    },
    "France": {
      code: "EUR",
      symbol: "€",
      taxRate: 0.20, // 20% VAT
    },
    "Italy": {
      code: "EUR",
      symbol: "€",
      taxRate: 0.22, // 22% VAT
    },
    "Spain": {
      code: "EUR",
      symbol: "€",
      taxRate: 0.21, // 21% VAT
    },
    "Netherlands": {
      code: "EUR",
      symbol: "€",
      taxRate: 0.21, // 21% VAT
    },
    "Belgium": {
      code: "EUR",
      symbol: "€",
      taxRate: 0.21, // 21% VAT
    },
    "Switzerland": {
      code: "CHF",
      symbol: "CHF",
      taxRate: 0.077, // 7.7% VAT
    },
    "Japan": {
      code: "JPY",
      symbol: "¥",
      taxRate: 0.10, // 10% consumption tax
    },
    "China": {
      code: "CNY",
      symbol: "¥",
      taxRate: 0.13, // 13% VAT
    },
    "India": {
      code: "INR",
      symbol: "₹",
      taxRate: 0.18, // 18% GST
    },
    "Singapore": {
      code: "SGD",
      symbol: "S$",
      taxRate: 0.09, // 9% GST
    },
    "Hong Kong": {
      code: "HKD",
      symbol: "HK$",
      taxRate: 0.00, // No VAT
    },
    "United Arab Emirates": {
      code: "AED",
      symbol: "AED",
      taxRate: 0.05, // 5% VAT
    },
    "Saudi Arabia": {
      code: "SAR",
      symbol: "SAR",
      taxRate: 0.15, // 15% VAT
    },
    "South Africa": {
      code: "ZAR",
      symbol: "R",
      taxRate: 0.15, // 15% VAT
    },
    "Brazil": {
      code: "BRL",
      symbol: "R$",
      taxRate: 0.17, // 17% average tax
    },
    "Mexico": {
      code: "MXN",
      symbol: "$",
      taxRate: 0.16, // 16% VAT
    },
    "Argentina": {
      code: "ARS",
      symbol: "$",
      taxRate: 0.21, // 21% VAT
    },
    "New Zealand": {
      code: "NZD",
      symbol: "NZ$",
      taxRate: 0.15, // 15% GST
    },
    "South Korea": {
      code: "KRW",
      symbol: "₩",
      taxRate: 0.10, // 10% VAT
    },
    "Thailand": {
      code: "THB",
      symbol: "฿",
      taxRate: 0.07, // 7% VAT
    },
    "Malaysia": {
      code: "MYR",
      symbol: "RM",
      taxRate: 0.10, // 10% SST
    },
    "Indonesia": {
      code: "IDR",
      symbol: "Rp",
      taxRate: 0.11, // 11% VAT
    },
    "Philippines": {
      code: "PHP",
      symbol: "₱",
      taxRate: 0.12, // 12% VAT
    },
    "Vietnam": {
      code: "VND",
      symbol: "₫",
      taxRate: 0.10, // 10% VAT
    },
    "Turkey": {
      code: "TRY",
      symbol: "₺",
      taxRate: 0.20, // 20% VAT
    },
    "Russia": {
      code: "RUB",
      symbol: "₽",
      taxRate: 0.20, // 20% VAT
    },
    "Poland": {
      code: "PLN",
      symbol: "zł",
      taxRate: 0.23, // 23% VAT
    },
    "Sweden": {
      code: "SEK",
      symbol: "kr",
      taxRate: 0.25, // 25% VAT
    },
    "Norway": {
      code: "NOK",
      symbol: "kr",
      taxRate: 0.25, // 25% VAT
    },
    "Denmark": {
      code: "DKK",
      symbol: "kr",
      taxRate: 0.25, // 25% VAT
    },
    "Finland": {
      code: "EUR",
      symbol: "€",
      taxRate: 0.24, // 24% VAT
    },
    "Ireland": {
      code: "EUR",
      symbol: "€",
      taxRate: 0.23, // 23% VAT
    },
    "Portugal": {
      code: "EUR",
      symbol: "€",
      taxRate: 0.23, // 23% VAT
    },
    "Greece": {
      code: "EUR",
      symbol: "€",
      taxRate: 0.24, // 24% VAT
    },
    "Austria": {
      code: "EUR",
      symbol: "€",
      taxRate: 0.20, // 20% VAT
    },
    "Czech Republic": {
      code: "CZK",
      symbol: "Kč",
      taxRate: 0.21, // 21% VAT
    },
    "Hungary": {
      code: "HUF",
      symbol: "Ft",
      taxRate: 0.27, // 27% VAT
    },
    "Romania": {
      code: "RON",
      symbol: "lei",
      taxRate: 0.19, // 19% VAT
    },
    "Israel": {
      code: "ILS",
      symbol: "₪",
      taxRate: 0.17, // 17% VAT
    },
    "Egypt": {
      code: "EGP",
      symbol: "E£",
      taxRate: 0.14, // 14% VAT
    },
    "Nigeria": {
      code: "NGN",
      symbol: "₦",
      taxRate: 0.075, // 7.5% VAT
    },
    "Kenya": {
      code: "KES",
      symbol: "KSh",
      taxRate: 0.16, // 16% VAT
    },
    "Chile": {
      code: "CLP",
      symbol: "$",
      taxRate: 0.19, // 19% VAT
    },
    "Colombia": {
      code: "COP",
      symbol: "$",
      taxRate: 0.19, // 19% VAT
    },
    "Peru": {
      code: "PEN",
      symbol: "S/",
      taxRate: 0.18, // 18% IGV
    },
    "Guyana": {
      code: "GYD",
      symbol: "GY$",
      taxRate: 0.14, // 14% VAT
    },
  };

  // Base currency and prices (set in CAD)
  const BASE_CURRENCY_CODE = "CAD";
  const BASE_PRICE_KURTA = 44.99; // Customized Kurtas (CAD)
  const BASE_PRICE_SHAWL = 33.99; // Shawls (CAD)
  const BASE_SHIPPING_CHARGES = 35.00; // Base shipping (CAD)
  const FIXED_TAX_RATE = 0.13; // Fixed tax rate (13% - Canada's rate) - all amounts stay the same

  // European countries list
  const EUROPEAN_COUNTRIES = [
    "United Kingdom", "Germany", "France", "Italy", "Spain", "Netherlands",
    "Belgium", "Switzerland", "Poland", "Sweden", "Norway", "Denmark",
    "Finland", "Ireland", "Portugal", "Greece", "Austria", "Czech Republic",
    "Hungary", "Romania", "Russia", "Turkey"
  ];

  // Helper function to format currency (some currencies don't use decimals)
  const formatCurrency = (amount, currencyCode) => {
    const noDecimalCurrencies = ["JPY", "KRW", "VND", "IDR", "CLP", "COP"];
    if (noDecimalCurrencies.includes(currencyCode)) {
      return Math.round(amount).toLocaleString();
    }
    return amount.toFixed(2);
  };

  // Get currency code based on country (same logic as KurtaPajamaShowcase)
  const getCurrencyForCountry = (country) => {
    // Canada → CAD
    if (country === "Canada") {
      return "CAD";
    }

    // European countries → GBP
    if (EUROPEAN_COUNTRIES.includes(country)) {
      return "GBP";
    }

    // All other countries → USD
    return "USD";
  };

  // Calculate pricing for an order based on country - prices stay same, only currency code changes
  const calculateOrderPricing = (order, orderNo) => {
    const country = selectedCountry[orderNo] || "Canada";
    const currencyCode = getCurrencyForCountry(country);
    
    const currency = {
      code: currencyCode,
      symbol: "", // No symbol, only code
      taxRate: FIXED_TAX_RATE, // Fixed tax rate
      exchangeRate: 1.0 // No conversion - same price
    };

    // Prices remain the same (no conversion)
    const pricePerKurta = BASE_PRICE_KURTA;
    const pricePerShawl = BASE_PRICE_SHAWL;
    const shippingCharges = BASE_SHIPPING_CHARGES;
    
    // Calculate price for each look (1 Kurta + 1 Shawl per outfit)
    const pricePerOutfit = pricePerKurta + pricePerShawl;
    
    // Calculate subtotal
    const subtotal = (order.totalQuantity || 0) * pricePerOutfit;
    
    // Calculate tax - using fixed rate
    const taxAmount = subtotal * FIXED_TAX_RATE;
    
    // Calculate total
    const total = subtotal + taxAmount + shippingCharges;
    
    return {
      currency,
      pricePerOutfit,
      subtotal,
      taxAmount,
      shippingCharges,
      total,
      pricePerKurta,
      pricePerShawl
    };
  };

  // Handler for updating shawl order address
  const handleShawlOrderAddressUpdate = async (orderId) => {
    if (!orderId) return;

    const address = shawlOrderAddress[orderId];
    if (!address) {
      toast.error("Please fill in the address fields");
      return;
    }

    if (!address.country || !address.state || !address.city || !address.houseNo || !address.contact) {
      toast.error("Country, state, city, house number, and contact are required");
      return;
    }

    setUpdatingShawlAddress(prev => ({ ...prev, [orderId]: true }));

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${base_url}/shawl-orders/${orderId}/address`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          country: address.country,
          state: address.state,
          city: address.city,
          houseNo: address.houseNo,
          street: address.street || "",
          landmark: address.landmark || "",
          contact: address.contact,
          currency: address.currency || "CAD",
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success("Address updated successfully!");
        // Refresh orders
        if (loggedInUser?.email) {
          fetchUserOrders(loggedInUser.email);
        }
      } else {
        toast.error(data.message || "Failed to update address");
      }
    } catch (err) {
      console.error("Error updating shawl order address:", err);
      toast.error("Error updating address. Please try again.");
    } finally {
      setUpdatingShawlAddress(prev => ({ ...prev, [orderId]: false }));
    }
  };

  // Handler for uploading payment proof for shawl orders
  const handleShawlOrderPaymentProofUpload = async (orderId, file) => {
    if (!orderId || !file) {
      toast.error("Please select a file to upload");
      return;
    }

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPEG, PNG images and PDF files are allowed");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setUploadingPaymentProof(prev => ({ ...prev, [orderId]: true }));

    try {
      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("paymentProof", file);

      const response = await fetch(`${base_url}/shawl-orders/${orderId}/payment-proof`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success("Payment proof uploaded successfully!");
        setShowShawlPaymentReviewPopup(true);
        // Refresh orders
        if (loggedInUser?.email) {
          fetchUserOrders(loggedInUser.email);
        }
      } else {
        toast.error(data.message || "Failed to upload payment proof");
      }
    } catch (err) {
      console.error("Error uploading payment proof:", err);
      toast.error("Error uploading payment proof. Please try again.");
    } finally {
      setUploadingPaymentProof(prev => ({ ...prev, [orderId]: false }));
    }
  };

  // Payment proof upload handler
  const handlePaymentProofUpload = async (orderNo, file) => {
    // Check if order is locked
    const lockStatus = orderLockStatus[orderNo];
    if (lockStatus?.isLocked) {
      toast.error("Order is locked. Cannot upload payment proof.");
      return;
    }

    // Prevent upload if order is confirmed
    if (isOrderConfirmed(orderNo)) {
      toast.error("This order has been confirmed and cannot be modified");
      return;
    }

    if (!file) {
      toast.error("Please select a file to upload");
      return;
    }

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPEG, PNG images and PDF files are allowed");
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setUploadingPaymentProof(prev => ({ ...prev, [orderNo]: true }));

    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user"));
      const formData = new FormData();
      formData.append("paymentProof", file);
      formData.append("orderNo", orderNo);

      // First upload the file to get the path
      const uploadResponse = await fetch(`${base_url}/payment_proof/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const uploadData = await uploadResponse.json();

      // Check for both 'path' and 'filePath' (API returns 'filePath')
      const paymentProofPath = uploadData.path || uploadData.filePath;

      if (uploadResponse.ok && paymentProofPath) {
        // Then use the new endpoint to update payment proof
        const response = await fetch(`${base_url}/orders/${orderNo}/payment-proof`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            paymentProof: paymentProofPath,
            userEmail: user?.email
          }),
      });

      const data = await response.json();

        if (response.ok && data.success) {
        toast.success(data.message || "Payment proof uploaded successfully!");
          // Refresh the order data and lock status
        if (loggedInUser?.email) {
          fetchAdminOrders(loggedInUser.email);
            fetchOrderLockStatus(orderNo);
        }
      } else {
          // Check if it's actually an error or just a different response format
          if (data.error) {
        toast.error(data.error || "Failed to upload payment proof");
          } else if (data.message) {
            // Sometimes API returns success with message instead of success flag
            toast.success(data.message || "Payment proof uploaded successfully!");
            if (loggedInUser?.email) {
              fetchAdminOrders(loggedInUser.email);
              fetchOrderLockStatus(orderNo);
            }
          } else {
            toast.error("Failed to upload payment proof");
          }
        }
      } else {
        // Check if upload was actually successful but response format is different
        if (uploadData.success && uploadData.filePath) {
          // Upload was successful, try to proceed with the second step
          const paymentProofPath = uploadData.filePath;
          const response = await fetch(`${base_url}/orders/${orderNo}/payment-proof`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              paymentProof: paymentProofPath,
              userEmail: user?.email
            }),
          });

          const data = await response.json();
          if (response.ok && data.success) {
            toast.success(data.message || "Payment proof uploaded successfully!");
            if (loggedInUser?.email) {
              fetchAdminOrders(loggedInUser.email);
              fetchOrderLockStatus(orderNo);
            }
          } else if (data.message) {
            toast.success(data.message || "Payment proof uploaded successfully!");
            if (loggedInUser?.email) {
              fetchAdminOrders(loggedInUser.email);
              fetchOrderLockStatus(orderNo);
            }
          } else {
            toast.error(data.error || "Failed to update payment proof");
          }
        } else {
          toast.error(uploadData.error || uploadData.message || "Failed to upload payment proof file");
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setUploadingPaymentProof(prev => ({ ...prev, [orderNo]: false }));
    }
  };


  // Fetch countries for billing form
  useEffect(() => {
    fetch("https://countriesnow.space/api/v0.1/countries/states")
      .then((res) => res.json())
      .then((data) => {
        if (data.data && Array.isArray(data.data)) setCountries(data.data);
      })
      .catch((err) => { });
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const user = localStorage.getItem("user");

    if (!token || !user) {
      toast.error("Please login to view your profile");
      setTimeout(() => {
        navigate("/login");
      }, 1500);
      return;
    }

    try {
      const userData = JSON.parse(user);
      setLoggedInUser(userData);

      // Check for URL parameters to set active tab
      const tabParam = searchParams.get("tab");
      const orderNoParam = searchParams.get("orderNo");

      if (tabParam) {
        setActiveTab(tabParam);
      }

      // Fetch all data
      const fetchPromises = [
        fetchUserSubmissions(userData.email),
        fetchUserOrders(userData.email),
        fetchAdminOrders(userData.email) // Always fetch all admin orders
      ];

      // If orderNoParam exists, also fetch that specific order to ensure it's loaded
      if (orderNoParam) {
        fetchPromises.push(fetchOrderDetails(orderNoParam));
      }

      Promise.all(fetchPromises).finally(() => {
        setLoading(false);
      });
    } catch (err) {
      toast.error("Error loading user data");
    }
  }, [navigate, searchParams]);

  const fetchUserSubmissions = async (email) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${base_url}/measurements/user/${encodeURIComponent(email)}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await res.json();

      if (res.ok && data.success) {
        setSubmissions(data.submissions || []);
      } else {
        toast.error(data.error || "Failed to load submissions");
      }
    } catch (err) {
      toast.error("Error loading your measurements");
    }
  };

  const fetchUserOrders = async (email) => {
    try {
      const token = localStorage.getItem("token");
      
      // First try to fetch user-specific orders
      const res = await fetch(`${base_url}/orders/user/${encodeURIComponent(email)}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await res.json();
      console.log("Orders API Response:", data);
      
      // Also fetch all orders for fallback matching
      try {
        const allOrdersRes = await fetch(`${base_url}/orders/all-orders`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        const allOrdersData = await allOrdersRes.json();
        if (allOrdersRes.ok && allOrdersData.success) {
          // If user-specific orders are empty but we have orders with orderId, merge them
          if ((!data.orders || data.orders.length === 0) && allOrdersData.orders && allOrdersData.orders.length > 0) {
            // Get user's measurement order numbers from multiple sources
            const orderNosFromMeasurementOrders = measurementOrders.map(o => o.orderNo).filter(Boolean);
            const orderNosFromSubmissions = submissions.map(s => s.order_no || s.orderNo).filter(Boolean);
            const allUserOrderNos = [...new Set([...orderNosFromMeasurementOrders, ...orderNosFromSubmissions])];
            
            // Find orders where orderId matches user's measurement order numbers
            const matchingOrders = allOrdersData.orders.filter(order => {
              if (!order.orderId) return false;
              const orderIdStr = String(order.orderId).trim();
              return allUserOrderNos.some(orderNo => {
                const orderNoStr = String(orderNo).trim();
                return orderIdStr === orderNoStr || 
                             parseInt(orderIdStr) === parseInt(orderNoStr) ||
                             orderIdStr === String(parseInt(orderNoStr)) ||
                             String(parseInt(orderIdStr)) === orderNoStr;
              });
            });
            
            if (matchingOrders.length > 0) {
              // Merge with existing orders (if any) and set state
              const mergedOrders = [...(data.orders || []), ...matchingOrders];
              setOrders(mergedOrders);
              // Do not return here; we still want to try fetching shawl orders below
            }
          }
        }
      } catch (allOrdersErr) {
        // Silently fail - this is optional fallback
      }

      if (res.ok && data.success) {
        setOrders(data.orders || []);
      } else {
        // Show error only if it's not a "no orders" case
        if (data.error && !data.error.includes("not found")) {
          toast.error(data.error || "Failed to load orders");
        }
        setOrders([]);
      }

      // Fetch shawl orders for this user and merge into orders list
      try {
        const shawlRes = await fetch(`${base_url}/shawl-orders/user/${encodeURIComponent(email)}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const shawlData = await shawlRes.json();
        console.log("Shawl Orders API Response:", shawlData);

        if (shawlRes.ok && shawlData.success && Array.isArray(shawlData.orders) && shawlData.orders.length > 0) {
          const mappedShawlOrders = shawlData.orders.map((order) => {
            let items = [];
            try {
              items = Array.isArray(order.items)
                ? order.items
                : order.items
                ? JSON.parse(order.items)
                : [];
            } catch (e) {
              console.error("Error parsing shawl order items:", e);
              items = [];
            }

            const transformedItems = items.map((item) => ({
              name: "Premium Kashmiri Shawl",
              title: "Premium Kashmiri Shawl",
              quantity: item.quantity || 1,
              price: item.price || 0,
            }));

            return {
              id: order.id,
              order_date: order.order_date || order.created_at,
              total: order.total,
              items: transformedItems,
              // Map address fields from shawl order
              houseNo: order.houseNo || "",
              street: order.street || "",
              city: order.city || "",
              state: order.state || "",
              country: order.country || "",
              contact: order.contact || "",
              landmark: order.landmark || "",
              currency: order.currency || "CAD",
              payment_proof: order.payment_proof || null,
              payment_proof_uploaded_at: order.payment_proof_uploaded_at || null,
              address_submitted: order.address_submitted || 0,
              payment_status: order.payment_status || "pending",
              subtotal: order.subtotal || 0,
              tax_amount: order.tax_amount || 0,
              shipping_charges: order.shipping_charges || 0,
              isShawlOrder: true, // Flag to identify shawl orders
            };
          });

          setOrders((prev) => {
            const existingIds = new Set(prev.map((o) => o.id));
            const newOnes = mappedShawlOrders.filter((o) => !existingIds.has(o.id));
            
            // Initialize address state for shawl orders
            mappedShawlOrders.forEach(order => {
              if (order.id && (order.country || order.state || order.city || order.houseNo || order.contact)) {
                setShawlOrderAddress(prevAddr => ({
                  ...prevAddr,
                  [order.id]: {
                    country: order.country || "",
                    state: order.state || "",
                    city: order.city || "",
                    houseNo: order.houseNo || "",
                    street: order.street || "",
                    landmark: order.landmark || "",
                    contact: order.contact || "",
                    currency: order.currency || "CAD",
                  }
                }));
              }
            });
            
            return [...prev, ...newOnes];
          });
        }
      } catch (shawlErr) {
        console.error("Error fetching shawl orders:", shawlErr);
      }
    } catch (err) {
      toast.error("Error loading orders. Please try again.");
      setOrders([]);
    }
  };

  const fetchAdminOrders = async (email) => {
    try {
      const token = localStorage.getItem("token");
      // Fetch orders where user is admin
      const res = await fetch(`${base_url}/measurements/admin/${encodeURIComponent(email)}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await res.json();
      console.log("Admin Measurements API Response:", data);

      if (res.ok && data.success) {
        // Ensure all orders have proper structure
        const normalizedOrders = (data.orders || []).map(order => {
          const normalized = {
            orderNo: order.orderNo || order.order_no,
            adminName: order.adminName || order.admin_name || '',
            adminEmail: order.adminEmail || order.admin_email || '',
            totalQuantity: order.totalQuantity || order.total_quantity || 0,
            orderDate: order.orderDate || order.order_date || order.created_at,
            paymentProof: order.paymentProof || order.payment_proof,
            paymentProofUploadedAt: order.paymentProofUploadedAt || order.payment_proof_uploaded_at,
            looks: order.looks || []
          };
          // Debug: log if data is missing
          if (!normalized.orderNo || !normalized.adminName || !normalized.adminEmail) {
          }
          return normalized;
        });
        setMeasurementOrders(normalizedOrders);
      }
    } catch (err) {
      // Error fetching admin orders
    }
  };

  // Fetch lock status for all submitted measurement orders (useEffect to avoid render loop)
  // This checks the orders table to see if orderId matches order_no
  useEffect(() => {
    if (!measurementOrders || measurementOrders.length === 0) return;
    
    const submittedOrders = measurementOrders.filter(order => areAllRecipientsSubmitted(order));
    
    submittedOrders.forEach(order => {
      if (order.orderNo) {
        const lockStatus = orderLockStatus[order.orderNo];
        const isFetching = fetchingLockStatus.has(order.orderNo);
        // Only fetch if not already fetched and not currently fetching
        // This API call checks the orders table where orderId = orderNo
        if ((!lockStatus || (!lockStatus.status && lockStatus.order === undefined)) && !isFetching) {
          fetchOrderLockStatus(order.orderNo);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measurementOrders.length, activeTab]); // Also run when Orders tab becomes active

  const fetchOrderDetails = async (orderNo) => {
    if (!orderNo) {
      console.error("fetchOrderDetails: orderNo is required");
      return;
    }
    
    // Prevent duplicate fetches
    if (fetchingOrderDetails.has(orderNo)) {
      return;
    }
    
    // Mark as fetching
    setFetchingOrderDetails(prev => new Set(prev).add(orderNo));
    
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${base_url}/measurements/order/${orderNo}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await res.json();

      if (res.ok && data.success && data.order) {
        // Add or update this order in measurementOrders
        setMeasurementOrders(prev => {
          // Normalize orderNo to number for comparison
          const newOrderNo = parseInt(data.order.orderNo) || data.order.orderNo;
          const exists = prev.find(o => {
            const existingOrderNo = parseInt(o.orderNo) || o.orderNo;
            return existingOrderNo === newOrderNo;
          });
          if (!exists) {
            // Normalize the new order data
            const normalizedOrder = {
              orderNo: data.order.orderNo || data.order.order_no,
              adminName: data.order.adminName || data.order.admin_name || '',
              adminEmail: data.order.adminEmail || data.order.admin_email || '',
              totalQuantity: data.order.totalQuantity || data.order.total_quantity || 0,
              orderDate: data.order.orderDate || data.order.order_date,
              paymentProof: data.order.paymentProof || data.order.payment_proof,
              paymentProofUploadedAt: data.order.paymentProofUploadedAt || data.order.payment_proof_uploaded_at,
              looks: data.order.looks || []
            };
            // Add new order at the beginning (most recent first)
            return [normalizedOrder, ...prev];
          }
          // Update existing order while preserving order
          return prev.map(o => {
            const existingOrderNo = parseInt(o.orderNo) || o.orderNo;
            if (existingOrderNo === newOrderNo) {
              // Normalize the updated order data
                return {
                  orderNo: data.order.orderNo || data.order.order_no,
                  adminName: data.order.adminName || data.order.admin_name || '',
                  adminEmail: data.order.adminEmail || data.order.admin_email || '',
                totalQuantity: data.order.totalQuantity || data.order.total_quantity || 0,
                orderDate: data.order.orderDate || data.order.order_date,
                paymentProof: data.order.paymentProof || data.order.payment_proof,
                paymentProofUploadedAt: data.order.paymentProofUploadedAt || data.order.payment_proof_uploaded_at,
                looks: data.order.looks || []
              };
            }
            return o;
          });
        });
      } else {
        // Handle error response
        console.error("fetchOrderDetails error:", data.error || "Unknown error");
        if (res.status !== 404) {
          // Don't show error for 404 (order not found), just log it
          console.warn(`Order ${orderNo} not found or failed to load`);
        }
      }
    } catch (err) {
      console.error("fetchOrderDetails exception:", err);
      // Only show toast for unexpected errors, not for 404s
      if (err.message && !err.message.includes("404")) {
        toast.error("Error loading order details. Please try again.");
      }
    } finally {
      // Remove from fetching set
      setFetchingOrderDetails(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderNo);
        return newSet;
      });
    }
  };

  // Check if all recipients have submitted measurements for an order
  const areAllRecipientsSubmitted = (order) => {
    if (!order.looks || order.looks.length === 0) return false;

    // Check all looks
    for (const look of order.looks) {
      if (!look.recipients || look.recipients.length === 0) return false;

      // Check all recipients in this look
      for (const recipient of look.recipients) {
        if (recipient.status !== 'SUBMITTED') {
          return false;
        }
      }
    }

    return true;
  };

  // Calculate total quantity from recipients and update order
  const updateOrderTotalQuantity = (orderNo) => {
    setMeasurementOrders(prevOrders => {
      return prevOrders.map(order => {
        if (order.orderNo === orderNo || String(order.orderNo) === String(orderNo)) {
          // Count total recipients across all looks
          let totalRecipients = 0;
          if (order.looks && order.looks.length > 0) {
            order.looks.forEach(look => {
              if (look.recipients && look.recipients.length > 0) {
                totalRecipients += look.recipients.length;
              }
            });
          }
          
          // Update totalQuantity to match recipient count
          return {
            ...order,
            totalQuantity: totalRecipients
          };
        }
        return order;
      });
    });
  };

  // Check if a measurement order has been confirmed (exists in orders table)
  // Compares order_no from measurement_submissions table with orderId from orders table
  const isOrderConfirmed = (orderNo) => {
    if (!orderNo) {
      return false;
    }
    
    // First, check if any submission's order_no matches any orderId from orders table
    // AND that submission's order_no matches the current orderNo being checked
    if (submissions && submissions.length > 0 && orders && orders.length > 0) {
      // Find submissions that match the current orderNo
      const relevantSubmissions = submissions.filter(submission => {
        const submissionOrderNo = submission.order_no || submission.orderNo;
        if (!submissionOrderNo) return false;
        
        const submissionOrderNoStr = String(submissionOrderNo).trim();
        const orderNoStr = String(orderNo).trim();
        return submissionOrderNoStr === orderNoStr || 
               parseInt(submissionOrderNoStr) === parseInt(orderNoStr);
      });
      
      // Check if any of these submissions' order_no matches any orderId in orders table
      const submissionMatch = relevantSubmissions.some(submission => {
        const submissionOrderNo = submission.order_no || submission.orderNo;
        
        // Check if this submission's order_no matches any orderId in orders table
        const hasMatch = orders.some(order => {
          const orderIdValue = order.orderId !== undefined ? order.orderId : (order.order_id !== undefined ? order.order_id : null);
          
          if (orderIdValue !== null && orderIdValue !== undefined && orderIdValue !== '') {
            const orderIdStr = String(orderIdValue).trim();
            const submissionOrderNoStr = String(submissionOrderNo).trim();
            return orderIdStr === submissionOrderNoStr || 
                   parseInt(orderIdStr) === parseInt(submissionOrderNoStr) ||
                   orderIdStr === String(parseInt(submissionOrderNoStr)) ||
                   String(parseInt(orderIdStr)) === submissionOrderNoStr;
          }
          return false;
        });
        
        return hasMatch;
      });
      
      if (submissionMatch) {
        return true;
      }
    }
    
    if (!orders || orders.length === 0) {
      return false;
    }
    
    // Second, check if any order has orderId column matching this orderNo
    // This is the primary check - if orderId exists in orders table, order is confirmed
    const hasOrderIdMatch = orders.some(order => {
      // Check both orderId and order_id (in case of different naming from API)
      const orderIdValue = order.orderId !== undefined ? order.orderId : (order.order_id !== undefined ? order.order_id : null);
      
      if (orderIdValue !== null && orderIdValue !== undefined && orderIdValue !== '') {
        const orderIdStr = String(orderIdValue).trim();
        const orderIdNum = parseInt(orderIdValue);
        const orderNoStr = String(orderNo).trim();
        const orderNoNum = parseInt(orderNo);
        
        // Try multiple comparison methods
        return orderIdStr === orderNoStr || 
               orderIdNum === orderNoNum ||
               orderIdStr === String(orderNoNum) ||
               String(orderIdNum) === orderNoStr ||
               parseInt(orderIdStr) === parseInt(orderNoStr);
      }
      return false;
    });
    
    if (hasOrderIdMatch) {
      return true;
    }
    
    // Fallback: Check if any order in the orders list has items with this orderNo
    const hasItemMatch = orders.some(order => {
      const orderItems = order.items && typeof order.items === 'string' 
        ? JSON.parse(order.items) 
        : Array.isArray(order.items) 
          ? order.items 
          : [];
      
      // Check if any item is a measurement_order with matching orderNo
      return orderItems.some(item => 
        item.type === 'measurement_order' && 
        (String(item.orderNo) === String(orderNo) || parseInt(item.orderNo) === parseInt(orderNo))
      );
    });
    
    if (hasItemMatch) {
      return true;
    }
    
    return false;
  };

  // Get orderId from orders table for a given orderNo
  const getOrderIdFromOrders = (orderNo) => {
    if (!orderNo) {
      return null;
    }
    if (!orders || orders.length === 0) {
      return null;
    }
    
    // Normalize orderNo to both string and number for comparison
    const orderNoStr = String(orderNo).trim();
    const orderNoNum = parseInt(orderNo);
    
    // Find order where orderId matches orderNo
    const matchedOrder = orders.find(order => {
      // Check both orderId and order_id (in case of different naming from API)
      const orderIdValue = order.orderId !== undefined ? order.orderId : (order.order_id !== undefined ? order.order_id : null);
      
      if (orderIdValue !== null && orderIdValue !== undefined && orderIdValue !== '') {
        const orderIdStr = String(orderIdValue).trim();
        const orderIdNum = parseInt(orderIdValue);
        
        // Try multiple comparison methods
        return orderIdStr === orderNoStr || 
               orderIdNum === orderNoNum ||
               orderIdStr === String(orderNoNum) ||
               String(orderIdNum) === orderNoStr ||
               parseInt(orderIdStr) === parseInt(orderNoStr);
      }
      return false;
    });
    
    // Return the orderId value (check both possible field names)
    return matchedOrder ? (matchedOrder.orderId !== undefined ? matchedOrder.orderId : matchedOrder.order_id) : null;
  };

  // Get confirmed order details (billing information) from orders table
  const getConfirmedOrderDetails = (orderNo) => {
    if (!orderNo || !orders || orders.length === 0) return null;
    
    // First, try to find order by orderId column
    const orderById = orders.find(order => {
      if (order.orderId) {
        return String(order.orderId) === String(orderNo) || parseInt(order.orderId) === parseInt(orderNo);
      }
      return false;
    });
    
    if (orderById) return orderById;
    
    // Fallback: Find the order that matches this orderNo in items
    const confirmedOrder = orders.find(order => {
      const orderItems = order.items && typeof order.items === 'string' 
        ? JSON.parse(order.items) 
        : Array.isArray(order.items) 
          ? order.items 
          : [];
      
      return orderItems.some(item => 
        item.type === 'measurement_order' && 
        (String(item.orderNo) === String(orderNo) || parseInt(item.orderNo) === parseInt(orderNo))
      );
    });
    
    return confirmedOrder;
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    toast.success("Logged out successfully");
    setTimeout(() => {
      navigate("/login");
    }, 1000);
  };

  const handlePhoneUpdate = async (e) => {
    e.preventDefault();

    if (!newPhone.trim()) {
      setPhoneError("Please enter a phone number.");
      toast.error("Please enter a phone number.");
      return;
    }

    // Basic phone validation
    const phoneRegex = /^[0-9]{10,15}$/;
    const cleanPhone = newPhone.replace(/[\s-()]/g, "");
    if (!phoneRegex.test(cleanPhone)) {
      setPhoneError("Please enter a valid phone number (10-15 digits).");
      toast.error("Please enter a valid phone number (10-15 digits).");
      return;
    }

    setPhoneError("");

    setUpdatingPhone(true);
    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user"));

      if (!user?.email) {
        toast.error("User not found in local storage.");
        setUpdatingPhone(false);
        return;
      }

      const response = await fetch(`${base_url}/update_phone`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: user.email,
          phone: cleanPhone,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || "Phone number updated successfully!");
        // Update local storage user data
        const updatedUser = { ...user, phone: cleanPhone };
        localStorage.setItem("user", JSON.stringify(updatedUser));
        setLoggedInUser(updatedUser);
        setNewPhone("");
        setPhoneError("");
      } else {
        toast.error(data.error || data.message || "Failed to update phone number.");
      }
    } catch (err) {
      console.error("Error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setUpdatingPhone(false);
    }
  };

  // Billing information handlers
  const handleBillingInfoChange = (orderNo, field, value) => {
    // Prevent changes if order is confirmed
    if (isOrderConfirmed(orderNo)) {
      toast.error("This order has been confirmed and cannot be modified");
      return;
    }
    
    setBillingInfo(prev => ({
      ...prev,
      [orderNo]: {
        ...prev[orderNo],
        [field]: value
      }
    }));

    // Clear error for this field when user starts typing
    if (billingErrors[orderNo] && billingErrors[orderNo][field]) {
      setBillingErrors(prev => ({
        ...prev,
        [orderNo]: {
          ...prev[orderNo],
          [field]: ""
        }
      }));
    }
  };

  const handleBillingCountryChange = async (orderNo, countryName) => {
    // Prevent changes if order is confirmed
    if (isOrderConfirmed(orderNo)) {
      toast.error("This order has been confirmed and cannot be modified");
      return;
    }
    
    // Update billing info with the country name
    handleBillingInfoChange(orderNo, 'country', countryName);
    
    // Update selected country for pricing (both dropdowns use the same countries list)
    setSelectedCountry(prev => ({
      ...prev,
      [orderNo]: countryName
    }));
    
    // Find matching country in API countries list for states/cities (try exact match first, then case-insensitive)
    let countryData = countries.find((c) => c.name === countryName);
    if (!countryData) {
      // Try case-insensitive match
      countryData = countries.find((c) => c.name.toLowerCase() === countryName.toLowerCase());
    }
    if (!countryData) {
      // Try partial match
      countryData = countries.find((c) => 
        c.name.toLowerCase().includes(countryName.toLowerCase()) ||
        countryName.toLowerCase().includes(c.name.toLowerCase())
      );
    }
    setStates(prev => ({
      ...prev,
      [orderNo]: countryData ? countryData.states : []
    }));
    handleBillingInfoChange(orderNo, 'state', '');
    setCities(prev => ({
      ...prev,
      [orderNo]: []
    }));
    
    // Clear errors for country, state, and city when country changes
    if (billingErrors[orderNo]) {
      setBillingErrors(prev => ({
        ...prev,
        [orderNo]: {
          ...prev[orderNo],
          country: "",
          state: "",
          city: ""
        }
      }));
    }
  };

  const handleBillingStateChange = async (orderNo, stateName) => {
    // Prevent changes if order is confirmed
    if (isOrderConfirmed(orderNo)) {
      toast.error("This order has been confirmed and cannot be modified");
      return;
    }
    
    handleBillingInfoChange(orderNo, 'state', stateName);
    handleBillingInfoChange(orderNo, 'city', '');
    const billing = billingInfo[orderNo] || {};
    if (!billing.country || !stateName) return;
    
    // Clear errors for state and city when state changes
    if (billingErrors[orderNo]) {
      setBillingErrors(prev => ({
        ...prev,
        [orderNo]: {
          ...prev[orderNo],
          state: "",
          city: ""
        }
      }));
    }
    
    try {
      const res = await fetch(
        "https://countriesnow.space/api/v0.1/countries/state/cities",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ country: billing.country, state: stateName }),
        }
      );
      const data = await res.json();
      setCities(prev => ({
        ...prev,
        [orderNo]: Array.isArray(data.data) ? data.data : []
      }));
    } catch (err) {
    }
  };

  // Submit address for an order
  const handleSubmitAddress = async (orderNo, address, showToast = true) => {
    // Check if order is locked
    const lockStatus = orderLockStatus[orderNo];
    if (lockStatus?.isLocked) {
      if (showToast) {
        toast.error("Order is locked. Cannot update address.");
      }
      return false;
    }

    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user"));
      const response = await fetch(`${base_url}/orders/${orderNo}/address`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          address,
          userEmail: user?.email
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (showToast) {
          toast.success(data.message || "Address submitted successfully!");
        }
        // Refresh lock status
        fetchOrderLockStatus(orderNo);
        return true;
      } else {
        if (showToast) {
          toast.error(data.error || "Failed to submit address");
        }
        return false;
      }
    } catch (err) {
      console.error(err);
      if (showToast) {
        toast.error("Something went wrong. Please try again.");
      }
      return false;
    }
  };

  // Confirm order (final step)
  const handleConfirmOrder = async (order) => {
    const orderNo = order.orderNo;
    
    // Check if order is locked
    const lockStatus = orderLockStatus[orderNo];
    if (lockStatus?.isLocked) {
      toast.error("Order is locked. Cannot confirm order.");
      return;
    }

    // Check if order is already confirmed using lock status (more accurate than isOrderConfirmed)
    const currentLockStatus = orderLockStatus[orderNo];
    if (currentLockStatus?.status?.confirmed === true || currentLockStatus?.isLocked) {
      toast.error("This order has been confirmed and cannot be modified");
      return;
    }
    
    const billing = billingInfo[orderNo] || {};
    
    // Use contact from billing info or fallback to user's phone
    const contact = billing.contact || loggedInUser?.phone || "";

    // Validation
    const newErrors = {};
    let hasErrors = false;

    if (!billing.country) {
      newErrors.country = "Country is required";
      hasErrors = true;
    }
    // State and City are optional - will send "NA" if not provided
    if (!billing.houseNo) {
      newErrors.houseNo = "House/Flat Number is required";
      hasErrors = true;
    }
    if (!billing.street || !billing.street.trim()) {
      newErrors.street = "Street Address is required";
      hasErrors = true;
    }
    if (!contact) {
      newErrors.contact = "Contact Phone is required";
      hasErrors = true;
    }

    if (hasErrors) {
      setBillingErrors(prev => ({
        ...prev,
        [orderNo]: newErrors
      }));
      toast.error("Please fill all required billing fields");
      return;
    }

    // Clear errors if validation passes
    setBillingErrors(prev => ({
      ...prev,
      [orderNo]: {}
    }));

    // Calculate pricing using dynamic pricing from KurtaPajamaShowcase
    const pricing = calculateOrderPricing(order, orderNo);
    const subtotal = pricing.subtotal;
    // Get the total amount and currency from pricing calculation
    // Ensure total is a number for database storage
    let totalAmount = parseFloat(pricing.total.toFixed(2));
    const currencyCode = pricing.currency.code;
    
    // Validate totalAmount is a valid number
    if (isNaN(totalAmount) || totalAmount <= 0) {
      console.error("Invalid total amount calculated:", totalAmount, "from pricing:", pricing);
      toast.error("Error calculating order total. Please try again.");
      return;
    }
    
    // Debug: Log pricing details
    console.log("Order Pricing Details:", {
      orderNo,
      subtotal: pricing.subtotal,
      taxAmount: pricing.taxAmount,
      shippingCharges: pricing.shippingCharges,
      total: pricing.total,
      totalAmount,
      currencyCode,
      totalAmountType: typeof totalAmount
    });

    // Prepare items array with order details
    const items = order.looks?.map(look => ({
      type: 'measurement_order',
      orderNo: orderNo,
      lookName: look.lookName || look.look_name,
      lookTitle: look.lookTitle || look.look_title,
      image: look.image,
      kurtaColor: look.kurtaColor || look.kurta_color,
      pajamaColor: look.pajamaColor || look.pajama_color,
      kurtaFabric: look.kurtaFabric || look.kurta_fabric,
      pajamaFabric: look.pajamaFabric || look.pajama_fabric,
      shawl: look.shawl,
      quantity: look.quantity || 0,
      price: (look.quantity || 0) * pricing.pricePerOutfit // Price stays same (in CAD)
    })) || [];

    setConfirmingOrder(prev => ({ ...prev, [orderNo]: true }));

    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user"));
      
      // Step 1: Submit address (don't show toast, we'll show confirmation message instead)
      const addressSubmitted = await handleSubmitAddress(orderNo, {
        country: billing.country,
        state: billing.state || "NA",
        city: billing.city || "NA",
        houseNo: billing.houseNo,
        street: billing.street || "",
        contact: contact
      }, false); // Don't show toast when called from confirm order

      if (!addressSubmitted) {
        setConfirmingOrder(prev => ({ ...prev, [orderNo]: false }));
        return;
      }

      // Refresh lock status after address submission to get latest confirmation status (force refresh)
      const refreshedStatus = await fetchOrderLockStatus(orderNo, true);
      
      // Re-check if order is already confirmed after refreshing status
      // Use the returned data directly instead of state (which might be stale)
      if (refreshedStatus?.status?.confirmed === true || refreshedStatus?.isLocked) {
        toast.warning("This order has already been confirmed");
        setConfirmingOrder(prev => ({ ...prev, [orderNo]: false }));
        // Refresh orders to show updated status
        if (loggedInUser?.email) {
          fetchUserOrders(loggedInUser.email);
          fetchAdminOrders(loggedInUser.email);
        }
        return;
      }

      // Step 2: Create order in orders table (if not exists) with items
      const orderData = {
          orderNo: orderNo,
          items: items,
          address: {
            country: billing.country,
          state: billing.state || "NA",
          city: billing.city || "NA",
            houseNo: billing.houseNo,
            street: billing.street || "",
            contact: contact
          },
        total: totalAmount, // Total amount to save in orders table total column
        currency: currencyCode, // Currency code (CAD, USD, GBP, etc.)
          adminEmail: loggedInUser?.email || order.adminEmail || order.admin_email
      };
      
      // Debug: Log the order data being sent
      console.log("Creating order with data:", orderData);
      console.log("Total amount being sent:", totalAmount, "Type:", typeof totalAmount);
      
      const createOrderResponse = await fetch(`${base_url}/orders/measurement`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(orderData),
      });

      const createOrderData = await createOrderResponse.json();

      // Debug: Log the response
      console.log("Order creation response:", createOrderData);
      console.log("Response status:", createOrderResponse.ok);
      console.log("Response success field:", createOrderData.success);

      // Check both HTTP status and success field
      if (!createOrderResponse.ok || !createOrderData.success) {
        console.error("Failed to create order:", createOrderData);
        toast.error(createOrderData.message || createOrderData.error || "Failed to create order");
        setConfirmingOrder(prev => ({ ...prev, [orderNo]: false }));
        return;
      }
      
      // Verify that total was saved
      if (createOrderData.order) {
        console.log("Order created successfully. Total saved:", createOrderData.order.total, "Currency:", createOrderData.order.currency);
      } else {
        console.warn("Order created but order data not returned in response");
      }

      // Step 3: Confirm the order using new endpoint
      const confirmResponse = await fetch(`${base_url}/orders/${orderNo}/confirm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          userEmail: user?.email
        }),
      });

      const confirmData = await confirmResponse.json();

      if (confirmResponse.ok && confirmData.success) {
        toast.success(confirmData.message || "Order confirmed successfully!");
        // Show confirmation popup
        setShowConfirmPopup(true);
        // Refresh orders and lock status
        if (loggedInUser?.email) {
          fetchUserOrders(loggedInUser.email);
          fetchAdminOrders(loggedInUser.email);
          fetchOrderLockStatus(orderNo);
        }
        // Clear billing info for this order
        setBillingInfo(prev => {
          const updated = { ...prev };
          delete updated[orderNo];
          return updated;
        });
      } else {
        // Check if order is already confirmed
        if (confirmData.error && (confirmData.error.includes("already") || confirmData.error.includes("locked"))) {
          toast.warning(confirmData.error || "Order is already confirmed");
        } else {
          toast.error(confirmData.error || "Failed to confirm order");
        }
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setConfirmingOrder(prev => ({ ...prev, [orderNo]: false }));
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();

    // Validate passwords and set errors
    const newErrors = {};
    let hasErrors = false;

    if (!currentPassword.trim()) {
      newErrors.currentPassword = "Current password is required.";
      hasErrors = true;
    }

    if (!newPassword.trim()) {
      newErrors.newPassword = "New password is required.";
      hasErrors = true;
    } else if (newPassword.length < 6) {
      newErrors.newPassword = "Password must be at least 6 characters long.";
      hasErrors = true;
    } else if (newPassword.length > 50) {
      newErrors.newPassword = "Password must be less than 50 characters.";
      hasErrors = true;
    } else if (newPassword === currentPassword) {
      newErrors.newPassword = "New password must be different from current password.";
      hasErrors = true;
    }

    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Please confirm your new password.";
      hasErrors = true;
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = "New password and confirm password do not match.";
      hasErrors = true;
    }

    if (hasErrors) {
      setPasswordErrors(newErrors);
      toast.error("Please fix the errors in the form", { position: "top-right" });
      return;
    }

    setPasswordErrors({});

    setUpdating(true);
    try {
      const token = localStorage.getItem("token");
      const user = JSON.parse(localStorage.getItem("user"));

      if (!user?.email) {
        toast.error("User not found in local storage.");
        setUpdating(false);
        return;
      }

      const response = await fetch(`${base_url}/update_password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: user.email,
          old_pass: currentPassword,
          new_pass: newPassword,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || "Password updated successfully! Please login again.");
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setPasswordErrors({});
        setTimeout(() => {
          handleLogout();
        }, 2000);
      } else {
        toast.error(data.error || data.message || "Failed to update password.");
      }
    } catch (err) {
      console.error("Error:", err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }


  return (
    <div className="profile-container">
      {/* Profile Header */}
      <div className="profile-header">
        <div className="header-content">
          <div>
            <h1>Welcome, {loggedInUser?.name || "User"}!</h1>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="profile-tabs">
        <button
          className={`tab-btn ${activeTab === "account" ? "active" : ""}`}
          onClick={() => setActiveTab("account")}
        >
          Account
        </button>
        <button
          className={`tab-btn ${activeTab === "orders" ? "active" : ""}`}
          onClick={() => setActiveTab("orders")}
        >
          Orders
        </button>
        <button
          className={`tab-btn ${activeTab === "measurements" ? "active" : ""}`}
          onClick={() => setActiveTab("measurements")}
        >
          Measurements
        </button>
      </div>

      {/* Account Tab */}
      {activeTab === "account" && (
        <div className="tab-content">
          <div className="account-section-new">
            {/* Account Information Card */}
            <div className="account-info-card-new">
              <div className="account-card-header-new">
                <div className="account-header-icon-new">
                  <FaUser />
                </div>
                <h2 className="account-card-title-new">Account Information</h2>
              </div>
              <div className="account-info-list-new">
                <div className="account-info-row-new">
                  <div className="account-info-icon-box-new">
                    <FaUser className="account-info-icon-new" />
                  </div>
                  <div className="account-info-details-new">
                    <div className="account-info-label-new">Full Name</div>
                    <div className="account-info-value-new">{loggedInUser?.name || "N/A"}</div>
                  </div>
                </div>
                <div className="account-info-row-new">
                  <div className="account-info-icon-box-new">
                    <FaEnvelope className="account-info-icon-new" />
                  </div>
                  <div className="account-info-details-new">
                    <div className="account-info-label-new">Email Address</div>
                    <div className="account-info-value-new">{loggedInUser?.email || "N/A"}</div>
                  </div>
                </div>
                {loggedInUser?.phone && (
                  <div className="account-info-row-new">
                    <div className="account-info-icon-box-new">
                      <FontAwesomeIcon icon={faPhone} className="account-info-icon-new" />
                    </div>
                    <div className="account-info-details-new">
                      <div className="account-info-label-new">Phone Number</div>
                      <div className="account-info-value-new">{loggedInUser.phone}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Update Phone Number Card */}
            <div className="account-update-card-new">
              <div className="account-card-header-new">
                <div className="account-header-icon-new phone-icon-new">
                  <FontAwesomeIcon icon={faPhone} />
                </div>
                <h2 className="account-card-title-new">Update Phone Number</h2>
              </div>
              <form onSubmit={handlePhoneUpdate} className="account-update-form-new">
                <div className="account-form-group-new">
                  <label htmlFor="newPhone" className="account-form-label-new">
                    <FontAwesomeIcon icon={faPhone} className="account-label-icon-new" />
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="newPhone"
                    value={newPhone}
                    onChange={(e) => {
                      setNewPhone(e.target.value);
                      if (phoneError) {
                        setPhoneError("");
                      }
                    }}
                    placeholder={loggedInUser?.phone || "Enter your phone number"}
                    className={`account-form-input-new ${phoneError ? "account-input-error-new" : ""}`}
                    required
                  />
                  {phoneError && <span className="account-error-message-new">{phoneError}</span>}
                </div>
                <button type="submit" className="account-update-btn-new" disabled={updatingPhone}>
                  {updatingPhone ? "Updating..." : "Update Phone Number"}
                </button>
              </form>
            </div>

            {/* Change Password Card */}
            <div className="account-update-card-new">
              <div className="account-card-header-new">
                <div className="account-header-icon-new password-icon-new">
                  <FaLock />
                </div>
                <h2 className="account-card-title-new">Change Password</h2>
              </div>
              <form onSubmit={handlePasswordUpdate} className="account-update-form-new">
                <div className="account-form-group-new">
                  <label htmlFor="currentPassword" className="account-form-label-new">
                    <FaKey className="account-label-icon-new" />
                    Current Password
                  </label>
                  <div className="account-password-wrapper-new">
                    <input
                      type={showPassword.current ? "text" : "password"}
                      id="currentPassword"
                      value={currentPassword}
                      onChange={(e) => {
                        setCurrentPassword(e.target.value);
                        if (passwordErrors.currentPassword) {
                          setPasswordErrors({ ...passwordErrors, currentPassword: "" });
                        }
                      }}
                      placeholder="Enter your current password"
                      className={`account-form-input-new ${passwordErrors.currentPassword ? "account-input-error-new" : ""}`}
                      required
                    />
                    <button
                      type="button"
                      className="account-password-toggle-new"
                      onClick={() => setShowPassword({ ...showPassword, current: !showPassword.current })}
                      aria-label="Toggle password visibility"
                    >
                      {showPassword.current ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {passwordErrors.currentPassword && (
                    <span className="account-error-message-new">{passwordErrors.currentPassword}</span>
                  )}
                </div>

                <div className="account-form-group-new">
                  <label htmlFor="newPassword" className="account-form-label-new">
                    <FaKey className="account-label-icon-new" />
                    New Password
                  </label>
                  <div className="account-password-wrapper-new">
                    <input
                      type={showPassword.new ? "text" : "password"}
                      id="newPassword"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        if (passwordErrors.newPassword) {
                          setPasswordErrors({ ...passwordErrors, newPassword: "" });
                        }
                        if (passwordErrors.confirmPassword && e.target.value === confirmPassword) {
                          setPasswordErrors({ ...passwordErrors, confirmPassword: "" });
                        }
                      }}
                      placeholder="Enter your new password"
                      className={`account-form-input-new ${passwordErrors.newPassword ? "account-input-error-new" : ""}`}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      className="account-password-toggle-new"
                      onClick={() => setShowPassword({ ...showPassword, new: !showPassword.new })}
                      aria-label="Toggle password visibility"
                    >
                      {showPassword.new ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {passwordErrors.newPassword && (
                    <span className="account-error-message-new">{passwordErrors.newPassword}</span>
                  )}
                </div>

                <div className="account-form-group-new">
                  <label htmlFor="confirmPassword" className="account-form-label-new">
                    <FaKey className="account-label-icon-new" />
                    Confirm New Password
                  </label>
                  <div className="account-password-wrapper-new">
                    <input
                      type={showPassword.confirm ? "text" : "password"}
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        if (passwordErrors.confirmPassword) {
                          setPasswordErrors({ ...passwordErrors, confirmPassword: "" });
                        }
                        if (passwordErrors.confirmPassword && e.target.value === newPassword) {
                          setPasswordErrors({ ...passwordErrors, confirmPassword: "" });
                        }
                      }}
                      placeholder="Confirm your new password"
                      className={`account-form-input-new ${passwordErrors.confirmPassword ? "account-input-error-new" : ""}`}
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      className="account-password-toggle-new"
                      onClick={() => setShowPassword({ ...showPassword, confirm: !showPassword.confirm })}
                      aria-label="Toggle password visibility"
                    >
                      {showPassword.confirm ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                  {passwordErrors.confirmPassword && (
                    <span className="account-error-message-new">{passwordErrors.confirmPassword}</span>
                  )}
                </div>

                <button type="submit" className="account-update-btn-new" disabled={updating}>
                  {updating ? "Updating..." : "Update Password"}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Orders Tab */}
      {activeTab === "orders" && (
        <div className="tab-content">
          <div className="orders-section">
            <h2>My Orders</h2>

            {orders.length === 0 && measurementOrders.filter(order => areAllRecipientsSubmitted(order)).length === 0 ? (
              <div className="no-orders">
                <p>You haven't placed any orders yet.</p>
                <button
                  onClick={() => navigate("/customization")}
                  className="btn-primary"
                >
                  Start Shopping
                </button>
              </div>
            ) : (
              <div className="orders-grid-revamped">
                {/* Regular Orders */}
                {orders.map((order, index) => {
                  const orderItems = order.items && typeof order.items === 'string' 
                    ? JSON.parse(order.items) 
                    : Array.isArray(order.items) 
                      ? order.items 
                      : [];
                  
                  const orderId = order.id || index;
                  const isExpanded = expandedOrders.has(orderId);
                  const isConfirmed = ["paid", "completed", "confirmed"].includes((order.payment_status || "").toLowerCase());
                  const isLocked = isConfirmed; // locked when already confirmed/paid
                  const orderPaymentProof = order.paymentProof || order.payment_proof;
                  const orderPaymentProofUploadedAt = order.paymentProofUploadedAt || order.payment_proof_uploaded_at;
                  const orderPaymentUploadKey = order.isShawlOrder ? order.id : order.orderNo;

                  return (
                    <div key={order.id || index} className={`order-card-revamped ${isExpanded ? 'expanded' : ''}`}>
                      {/* Header Section */}
                      <div 
                        className="order-header-revamped order-header-clickable"
                        onClick={() => {
                          setExpandedOrders(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(orderId)) {
                              newSet.delete(orderId);
                            } else {
                              newSet.add(orderId);
                            }
                            return newSet;
                          });
                        }}
                      >
                        <div className="order-header-main">
                          <div className="order-number-section">
                            <FaShoppingBag className="order-icon" />
                            <div>
                              <h3 className="order-number">Order #{order.id}</h3>
                              <div className="order-meta">
                                <span className="order-date-badge">
                                  <FaCalendarAlt />
                                  {order.order_date ? formatDate(order.order_date) : "N/A"}
                                </span>
                                <span className="order-status-badge status-completed">
                                  Completed
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="order-toggle-icon">
                          {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                        </div>
                      </div>

                      {/* Content Section */}
                      <div className={`order-content-revamped order-content-drawer ${isExpanded ? 'expanded' : ''}`}>
                        {/* Items Section */}
                        {orderItems.length > 0 && (
                          <div className="order-section-revamped">
                            <div className="section-header-revamped">
                              <FaBox className="section-icon" />
                              <h4>Order Items ({orderItems.length})</h4>
                            </div>
                            <div className="order-items-list-revamped">
                              {orderItems.map((item, idx) => (
                                <div key={idx} className="order-item-card-revamped">
                                  <div className="item-info-revamped">
                                    <h5 className="item-name-revamped">
                                      {item.name || item.title || "Item"}
                                    </h5>
                                    <div className="item-details-revamped">
                                      {item.color && (
                                        <span className="item-detail-tag">
                                          {item.color}
                                        </span>
                                      )}
                                      {item.size && (
                                        <span className="item-detail-tag">
                                          Size: {item.size}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="item-qty-price-revamped">
                                    <span className="item-quantity-revamped">
                                      Qty: {item.quantity || item.qty || 1}
                                    </span>
                                    {item.price && (
                                      <span className="item-price-revamped">
                                        {((item.price * (item.quantity || item.qty || 1)).toFixed(2))} CAD
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Address & Summary Grid */}
                        <div className="order-details-grid-revamped">
                          {/* Delivery Address */}
                          <div className="order-section-revamped address-section-revamped">
                            <div className="section-header-revamped">
                              <FaMapMarkerAlt className="section-icon" />
                              <h4>Delivery Address</h4>
                            </div>
                            {order.isShawlOrder ? (
                              // Editable address form for shawl orders
                              <div className="billing-form" style={{ marginTop: "15px" }}>
                                <div className="form-group">
                                  <label>Country <span className="required-asterisk">*</span></label>
                                  <select
                                    value={shawlOrderAddress[order.id]?.country || order.country || ""}
                                    onChange={(e) => {
                                      const countryName = e.target.value;
                                      setShawlOrderAddress(prev => ({
                                        ...prev,
                                        [order.id]: {
                                          ...prev[order.id],
                                          country: countryName,
                                          state: "",
                                          city: "",
                                        }
                                      }));
                                      // Load states for selected country
                                      const countryData = countries.find(c => c.name === countryName);
                                      if (countryData) {
                                        setStates(prev => ({
                                          ...prev,
                                          [order.id]: countryData.states || []
                                        }));
                                      }
                                    }}
                                  >
                                    <option value="">Select Country</option>
                                    {countries.map((c, idx) => (
                                      <option key={`${c.name}-${idx}`} value={c.name}>
                                        {c.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="form-group">
                                  <label>State <span className="required-asterisk">*</span></label>
                                  <select
                                    value={shawlOrderAddress[order.id]?.state || order.state || ""}
                                    onChange={(e) => {
                                      setShawlOrderAddress(prev => ({
                                        ...prev,
                                        [order.id]: {
                                          ...prev[order.id],
                                          state: e.target.value,
                                          city: "",
                                        }
                                      }));
                                      // Load cities for selected state
                                      const stateName = e.target.value;
                                      const countryName = shawlOrderAddress[order.id]?.country || order.country;
                                      if (stateName && countryName) {
                                        fetch(`https://countriesnow.space/api/v0.1/countries/state/cities`)
                                          .then(res => res.json())
                                          .then(data => {
                                            if (data.data && Array.isArray(data.data)) {
                                              const stateCities = data.data.find(s => s.state === stateName);
                                              setCities(prev => ({
                                                ...prev,
                                                [order.id]: stateCities ? stateCities.cities : []
                                              }));
                                            }
                                          })
                                          .catch(() => {});
                                      }
                                    }}
                                  >
                                    <option value="">Select State</option>
                                    {(states[order.id] || []).map((s, idx) => (
                                      <option key={`${s.name}-${idx}`} value={s.name}>
                                        {s.name}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <div className="form-group">
                                  <label>City <span className="required-asterisk">*</span></label>
                                  <input
                                    type="text"
                                    value={shawlOrderAddress[order.id]?.city || order.city || ""}
                                    onChange={(e) => {
                                      setShawlOrderAddress(prev => ({
                                        ...prev,
                                        [order.id]: {
                                          ...prev[order.id],
                                          city: e.target.value
                                        }
                                      }));
                                    }}
                                    placeholder="City"
                                  />
                                </div>
                                <div className="form-group">
                                  <label>House Number <span className="required-asterisk">*</span></label>
                                  <input
                                    type="text"
                                    value={shawlOrderAddress[order.id]?.houseNo || order.houseNo || ""}
                                    onChange={(e) => {
                                      setShawlOrderAddress(prev => ({
                                        ...prev,
                                        [order.id]: {
                                          ...prev[order.id],
                                          houseNo: e.target.value
                                        }
                                      }));
                                    }}
                                    placeholder="House/Apartment Number"
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Street</label>
                                  <input
                                    type="text"
                                    value={shawlOrderAddress[order.id]?.street || order.street || ""}
                                    onChange={(e) => {
                                      setShawlOrderAddress(prev => ({
                                        ...prev,
                                        [order.id]: {
                                          ...prev[order.id],
                                          street: e.target.value
                                        }
                                      }));
                                    }}
                                    placeholder="Street Address"
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Landmark</label>
                                  <input
                                    type="text"
                                    value={shawlOrderAddress[order.id]?.landmark || order.landmark || ""}
                                    onChange={(e) => {
                                      setShawlOrderAddress(prev => ({
                                        ...prev,
                                        [order.id]: {
                                          ...prev[order.id],
                                          landmark: e.target.value
                                        }
                                      }));
                                    }}
                                    placeholder="Nearby Landmark (Optional)"
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Contact Number <span className="required-asterisk">*</span></label>
                                  <input
                                    type="text"
                                    value={shawlOrderAddress[order.id]?.contact || order.contact || ""}
                                    onChange={(e) => {
                                      setShawlOrderAddress(prev => ({
                                        ...prev,
                                        [order.id]: {
                                          ...prev[order.id],
                                          contact: e.target.value
                                        }
                                      }));
                                    }}
                                    placeholder="Phone Number"
                                  />
                                </div>
                                <div className="form-group">
                                  <label>Currency</label>
                                  <select
                                    value={shawlOrderAddress[order.id]?.currency || order.currency || "CAD"}
                                    onChange={(e) => {
                                      setShawlOrderAddress(prev => ({
                                        ...prev,
                                        [order.id]: {
                                          ...prev[order.id],
                                          currency: e.target.value
                                        }
                                      }));
                                    }}
                                  >
                                    <option value="CAD">CAD</option>
                                    <option value="USD">USD</option>
                                    <option value="GBP">GBP</option>
                                    <option value="EUR">EUR</option>
                                  </select>
                                </div>
                                <button
                                  type="button"
                                  className="btn-primary"
                                  onClick={() => handleShawlOrderAddressUpdate(order.id)}
                                  disabled={updatingShawlAddress[order.id]}
                                  style={{ marginTop: "10px" }}
                                >
                                  {updatingShawlAddress[order.id] ? "Updating..." : "Update Address"}
                                </button>
                              </div>
                            ) : (
                              // Read-only address display for regular orders
                            <div className="address-details-revamped">
                              <p className="address-line-revamped">
                                {order.houseNo} {order.street}
                              </p>
                              <p className="address-line-revamped">
                                {order.city}, {order.state}
                              </p>
                              <p className="address-line-revamped">{order.country}</p>
                              {order.contact && (
                                <p className="address-extra-revamped contact-info-revamped">
                                  <FontAwesomeIcon icon={faPhone} />
                                  <span>{order.contact}</span>
                                </p>
                              )}
                            </div>
                            )}
                          </div>

                          {/* Order Summary */}
                          <div className="order-section-revamped summary-section-revamped">
                            <div className="section-header-revamped">
                              <FaDollarSign className="section-icon" />
                              <h4>Order Summary</h4>
                            </div>
                            <div className="summary-details-revamped">
                              {order.isShawlOrder ? (
                                // Detailed breakdown for shawl orders
                                <>
                                  {orderItems.map((item, idx) => (
                                    <div key={idx} className="summary-row-revamped">
                                      <span>{item.name} (Qty: {item.quantity})</span>
                                      <span>{((item.price * item.quantity) || 0).toFixed(2)} {order.currency || "CAD"}</span>
                                    </div>
                                  ))}
                                  <div className="summary-divider-revamped"></div>
                                  <div className="summary-row-revamped">
                                    <span>Subtotal:</span>
                                    <span>{(parseFloat(order.subtotal) || 0).toFixed(2)} {order.currency || "CAD"}</span>
                                  </div>
                                  <div className="summary-row-revamped">
                                    <span>Tax (HST/GST):</span>
                                    <span>{(parseFloat(order.tax_amount) || 0).toFixed(2)} {order.currency || "CAD"}</span>
                                  </div>
                                  <div className="summary-row-revamped">
                                    <span>Shipping:</span>
                                    <span>{(parseFloat(order.shipping_charges) || 0).toFixed(2)} {order.currency || "CAD"}</span>
                                  </div>
                                  <div className="summary-divider-revamped"></div>
                                  <div className="summary-total-revamped">
                                    <span>Total Amount</span>
                                    <span className="total-amount-revamped">
                                      {(parseFloat(order.total) || 0).toFixed(2)} {order.currency || "CAD"}
                                    </span>
                                  </div>
                                  <div className="summary-row-revamped" style={{ marginTop: "10px", paddingTop: "10px", borderTop: "1px solid #dee2e6" }}>
                                    <span>Payment Status:</span>
                                    <span style={{ 
                                      color: order.payment_status === "paid" ? "#28a745" : "#ffc107",
                                      fontWeight: "bold"
                                    }}>
                                      {order.payment_status === "paid" ? "Paid" : "Pending"}
                                    </span>
                                  </div>
                                   {/* Bank Account Details */}
                                    <div className="bank-details-section">
                                      <h5>Payment Details</h5>
                                      <div className="bank-info">
                                          <div className="bank-note">
                                            <div className="bank-note-header">
                                              <FaInfoCircle className="bank-note-icon" />
                                              <h6>Complete Your Payment</h6>
                                            </div>
                                            <div className="bank-note-content">
                                              <p>To complete your order, please submit an e-transfer to:</p>
                                              <div className="bank-email">
                                                <FaEnvelope className="email-icon" />
                                                <span className="bank-email-text">
                                                  <strong>info@shawlhaus.com</strong>
                                                </span>
                                              </div>
                                              <p className="bank-note-footer">Once the e-transfer has been completed, kindly upload proof of payment in the box below.</p>
                                            </div>
                                          </div>
                                      </div>
                                      
                                      {/* Payment Proof Upload Section */}
                                      <div className="payment-proof-upload-section">
                                        <h5>Upload Payment Proof</h5>
                                        {orderPaymentProof ? (
                                          <div className="payment-proof-uploaded">
                                            <div className="proof-status">
                                              <FaFileUpload className="proof-icon" />
                                              <div>
                                                <p><strong>Proof Uploaded Successfully</strong></p>
                                                <a 
                                                  href={`${base_url.replace("/apis", "")}${orderPaymentProof}`} 
                                                  target="_blank" 
                                                  rel="noopener noreferrer"
                                                  className="view-proof-link"
                                                >
                                                  View Proof
                                                </a>
                                                <p className="upload-date">
                                                  Uploaded on: {orderPaymentProofUploadedAt ? formatDate(orderPaymentProofUploadedAt) : 'N/A'}
                                                </p>
                                              </div>
                                            </div>
                                              {!isConfirmed && (
                                              <button
                                                type="button"
                                                className="btn-secondary upload-new-proof-btn"
                                                onClick={() => {
                                                  const fileInput = document.getElementById(`payment-proof-input-${orderId}`);
                                                  if (fileInput) fileInput.click();
                                                }}
                                              >
                                                <FaUpload /> Upload New Proof
                                              </button>
                                            )}
                                          </div>
                                        ) : (
                                          <div className="payment-proof-upload-form">
                                            <label htmlFor={`payment-proof-input-${orderId}`} className="upload-label">
                                              <FaUpload className="upload-icon" />
                                              <span>Select Payment Proof File</span>
                                              <small>JPEG, PNG, or PDF (Max 5MB)</small>
                                            </label>
                                            <input
                                              type="file"
                                              id={`payment-proof-input-${orderId}`}
                                              accept="image/jpeg,image/jpg,image/png,application/pdf"
                                              onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                  if (order.isShawlOrder) { handleShawlOrderPaymentProofUpload(order.id, file); } else { handlePaymentProofUpload(order.orderNo, file); }
                                                }
                                              }}
                                              style={{ display: 'none' }}
                                                disabled={isLocked || isConfirmed}
                                            />
                                              {!isLocked && !isConfirmed && (
                                              <button
                                                type="button"
                                                className="btn-primary upload-proof-btn"
                                                onClick={() => {
                                                  const fileInput = document.getElementById(`payment-proof-input-${orderId}`);
                                                  if (fileInput) fileInput.click();
                                                }}
                                                disabled={uploadingPaymentProof[orderPaymentUploadKey]}
                                              >
                                                {uploadingPaymentProof[orderPaymentUploadKey] ? (
                                                  <>Uploading...</>
                                                ) : (
                                                  <>
                                                    <FaUpload /> Upload Proof
                                                  </>
                                                )}
                                              </button>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                 
                                </>
                              ) : (
                                // Simple summary for regular orders
                                <>
                              <div className="summary-row-revamped">
                                <span>Items ({orderItems.length}):</span>
                                <span>{(parseFloat(order.total) || 0).toFixed(2)} CAD</span>
                              </div>
                              <div className="summary-divider-revamped"></div>
                              <div className="summary-total-revamped">
                                <span>Total Amount</span>
                                <span className="total-amount-revamped">
                                  {(parseFloat(order.total) || 0).toFixed(2)} CAD
                                </span>
                              </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Measurement Orders - Only show orders where all recipients have submitted */}
                {measurementOrders
                  .filter(order => areAllRecipientsSubmitted(order))
                  .map((order, idx) => {
                    const orderId = order.orderNo || idx;
                    const allSubmitted = areAllRecipientsSubmitted(order);
                    const orderIdFromOrders = getOrderIdFromOrders(order.orderNo); // Get orderId from orders table - checks orders state
                    const lockStatus = orderLockStatus[order.orderNo] || { isLocked: false, status: {}, order: null };
                    const isLocked = lockStatus.isLocked;
                    
                    // Check if order exists in orders table (orderId matches order_no)
                    // If lockStatus.order exists, it means the order was found in orders table
                    // Priority: 1) Lock status API order object (direct check from DB), 2) orderIdFromOrders (from orders state), 3) isOrderConfirmed
                    const orderExistsInOrdersTable = lockStatus.order !== null 
                      ? true 
                      : (!!orderIdFromOrders || isOrderConfirmed(order.orderNo));
                    
                    // Order is confirmed if it exists in orders table (orderId = order_no)
                    // OR if lock status explicitly says it's confirmed
                    const isConfirmed = orderExistsInOrdersTable || lockStatus.status?.confirmed === true;
                    const orderPaymentProof = order.paymentProof || order.payment_proof;
                    const orderPaymentProofUploadedAt = order.paymentProofUploadedAt || order.payment_proof_uploaded_at;
                    
                    // Ensure we have looks data - fetch if missing
                    if (allSubmitted && (!order.looks || order.looks.length === 0) && !fetchingOrderDetails.has(order.orderNo)) {
                      // Fetch order details if looks are missing and not already fetching
                      fetchOrderDetails(order.orderNo);
                    }
                    
                    // Fetch lock status if not already fetched (only once per order)
                    // This will be handled by useEffect to avoid render loop issues

                    const isMeasurementOrderExpanded = expandedOrders.has(`measurement-${orderId}`);
                    
                    return (
                      <div key={`measurement-order-${orderId}-${idx}`} className={`order-card measurement-order-in-orders ${allSubmitted ? 'all-submitted' : ''} ${isMeasurementOrderExpanded ? 'expanded' : ''}`}>
                        <div 
                          className={`card-header measurement-order-header-clickable ${allSubmitted ? 'all-submitted-header' : ''}`}
                          onClick={() => {
                            setExpandedOrders(prev => {
                              const newSet = new Set(prev);
                              const key = `measurement-${orderId}`;
                              if (newSet.has(key)) {
                                newSet.delete(key);
                              } else {
                                newSet.add(key);
                              }
                              return newSet;
                            });
                          }}
                        >
                          <div>
                            <h3>
                              Measurement Order #{order.orderNo || 'N/A'}
                              {orderIdFromOrders && (
                                <span style={{ 
                                  marginLeft: '8px', 
                                  fontSize: '0.85em', 
                                  color: '#6c757d',
                                  fontWeight: 'normal'
                                }}>
                                  (Order ID: {orderIdFromOrders})
                                </span>
                              )}
                            </h3>
                            <span className="order-date">
                              {order.orderDate
                                ? formatDate(order.orderDate)
                                : "N/A"}
                            </span>
                            {isLocked && (
                              <div className="order-locked-badge" style={{
                                display: 'inline-block',
                                marginLeft: '10px',
                                padding: '4px 12px',
                                background: '#dc3545',
                                color: 'white',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: 'bold'
                              }}>
                                🔒 Order Locked
                              </div>
                            )}
                            {isConfirmed && !isLocked && (
                              <div className="order-confirmed-badge" style={{
                                display: 'inline-block',
                                marginLeft: '10px',
                                padding: '4px 12px',
                                background: '#28a745',
                                color: 'white',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: 'bold'
                              }}>
                                ✅ Order Confirmed
                              </div>
                            )}
                            {allSubmitted && !isConfirmed && !isLocked && (
                              <div className="all-submitted-badge">
                                🎉 All Submitted ✅
                              </div>
                            )}
                          </div>
                          <div className="order-header-right">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveTab("measurements");
                                // Expand the order in measurements tab
                                setExpandedMeasurementOrders(prev => {
                                  const newSet = new Set(prev);
                                  newSet.add(orderId);
                                  return newSet;
                                });
                              }}
                              className="view-order-btn"
                            >
                              View This Order
                            </button>
                            <div className="order-toggle-icon">
                              {isMeasurementOrderExpanded ? <FaChevronUp /> : <FaChevronDown />}
                            </div>
                          </div>
                        </div>
                        <div className={`card-content order-content-drawer ${isMeasurementOrderExpanded ? 'expanded' : ''}`}>
                          <div className="order-items">
                            <h4>Details:</h4>
                            <div className="order-item">
                              <span>Total Outfits:</span>
                              <span>{order.totalQuantity || 0}</span>
                            </div>
                            <div className="order-item">
                              <span>Admin:</span>
                              <span>{order.adminName || order.adminEmail || 'N/A'}</span>
                            </div>
                            <div className="order-item">
                              <span>Status:</span>
                              <span className={allSubmitted ? 'status-ready' : 'status-pending'}>
                                {allSubmitted ? 'Ready for Processing ✅' : 'Pending Measurements'}
                              </span>
                            </div>
                          </div>
                          {allSubmitted && (
                            <div className="ready-message">
                              <strong>🎉 All recipients have submitted measurements! ✅</strong>
                              <p>Your order is ready for processing.</p>
                              
                              {isLocked && (
                                <div className="order-locked-message" style={{
                                  background: '#f8d7da',
                                  border: '2px solid #dc3545',
                                  borderRadius: '8px',
                                  padding: '20px',
                                  margin: '20px 0',
                                  textAlign: 'center'
                                }}>
                                  <h3 style={{ color: '#721c24', margin: '0 0 10px 0' }}>🔒 Order Locked</h3>
                                  <p style={{ color: '#721c24', margin: 0 }}>
                                    This order is locked. No further changes are allowed. Address, payment proof, and recipients cannot be modified.
                                  </p>
                                </div>
                              )}
                              {isConfirmed && !isLocked && (
                                <div className="order-confirmed-message" style={{
                                  background: '#d4edda',
                                  border: '2px solid #28a745',
                                  borderRadius: '8px',
                                  padding: '20px',
                                  margin: '20px 0',
                                  textAlign: 'center'
                                }}>
                                  <h3 style={{ color: '#155724', margin: '0 0 10px 0' }}>✅ Order Confirmed</h3>
                                  <p style={{ color: '#155724', margin: 0 }}>
                                    This order has been confirmed and is being processed. You cannot modify the details anymore.
                                  </p>
                                </div>
                              )}
                              
                              {/* Order Details Section - Always visible, but billing form only when not confirmed */}
                              <div className="order-confirmation-section">
                                <div className="order-confirmation-layout">
                                  {/* Left Side: Order Details */}
                                  <div className="order-details-left">
                                    <h4>Order Details</h4>
                                    <div className="order-details-content">
                                      {order.looks && order.looks.length > 0 ? order.looks.map((look, lookIdx) => {
                                        const imageUrl = look.image?.startsWith('http') 
                                          ? look.image 
                                          : look.image?.startsWith('/apis/')
                                            ? `${base_url.replace('/apis', '')}${look.image}`
                                            : `${base_url}/Looks/${look.image}`;
                                        
                                        return (
                                          <div key={lookIdx} className="order-look-item">
                                            {look.image && (
                                              <img 
                                                src={imageUrl} 
                                                alt={look.lookName || look.look_name || 'Look'} 
                                                className="order-look-image"
                                              />
                                            )}
                                            <div className="order-look-info">
                                              <h5>{look.lookName || look.look_name || 'Look'}</h5>
                                              {(look.lookTitle || look.look_title) && (
                                                <p className="look-title">{look.lookTitle || look.look_title}</p>
                                              )}
                                              <div className="look-specs">
                                                <p><strong>Kurta:</strong> {look.kurtaColor || look.kurta_color} • {look.kurtaFabric || look.kurta_fabric}</p>
                                                <p><strong>Pajama:</strong> {look.pajamaColor || look.pajama_color} • {look.pajamaFabric || look.pajama_fabric}</p>
                                                <p><strong>Shawl:</strong> {look.shawl}</p>
                                                <p><strong>Quantity:</strong> {look.quantity || 0} outfit(s)</p>
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      }) : (
                                        <p>Loading order details...</p>
                                      )}
                                    </div>

                                    {/* Recipient Management Section - Only show when not confirmed */}
                                    {!isConfirmed && order.looks && order.looks.length > 0 && (
                                      <div className="recipients-management-section" style={{
                                        marginTop: '30px',
                                        padding: window.innerWidth <= 768 ? '16px' : '25px',
                                        background: '#ffffff',
                                        borderRadius: '12px',
                                        border: '1px solid #e9ecef',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                                      }}>
                                        <div style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '10px',
                                          marginBottom: '25px',
                                          paddingBottom: '15px',
                                          borderBottom: '2px solid #b5651d'
                                        }}>
                                          <h4 style={{
                                            margin: 0,
                                            color: '#b5651d',
                                            fontSize: '20px',
                                            fontWeight: '700'
                                          }}>
                                            Recipient Management
                                          </h4>
                                        </div>
                                        
                                        {order.looks.map((look, lookIdx) => (
                                          <div key={lookIdx} style={{
                                            marginBottom: lookIdx < order.looks.length - 1 ? '25px' : '0',
                                            padding: window.innerWidth <= 768 ? '16px' : '20px',
                                            background: '#f8f9fa',
                                            borderRadius: '10px',
                                            border: '1px solid #e9ecef',
                                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                                          }}>
                                            <div style={{ 
                                              display: 'flex', 
                                              justifyContent: 'space-between', 
                                              alignItems: window.innerWidth <= 768 ? 'flex-start' : 'center',
                                              flexDirection: window.innerWidth <= 768 ? 'column' : 'row',
                                              gap: window.innerWidth <= 768 ? '12px' : '0',
                                              marginBottom: '20px',
                                              paddingBottom: '12px',
                                              borderBottom: '1px solid #dee2e6'
                                            }}>
                                              <h5 style={{ 
                                                margin: 0, 
                                                color: '#b5651d',
                                                fontSize: window.innerWidth <= 768 ? '14px' : '16px',
                                                fontWeight: '600'
                                              }}>
                                                {look.lookName || look.look_name || 'Look'}
                                              </h5>
                                              {!isLocked && !isConfirmed && (
                                                <button
                                                  className="add-recipient-btn"
                                                  onClick={() => setAddingRecipient({ orderNo: order.orderNo, lookId: look.lookId || look.id })}
                                                  disabled={isLocked || isConfirmed}
                                                  style={{
                                                    padding: window.innerWidth <= 768 ? '10px 16px' : '10px 20px',
                                                    background: (isLocked || isConfirmed) ? '#6c757d' : '#b5651d',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    cursor: (isLocked || isConfirmed) ? 'not-allowed' : 'pointer',
                                                    fontSize: window.innerWidth <= 768 ? '13px' : '14px',
                                                    fontWeight: '600',
                                                    opacity: (isLocked || isConfirmed) ? 0.6 : 1,
                                                    transition: 'all 0.2s ease',
                                                    boxShadow: (isLocked || isConfirmed) ? 'none' : '0 2px 4px rgba(181, 101, 29, 0.2)',
                                                    width: window.innerWidth <= 768 ? '100%' : 'auto'
                                                  }}
                                                  onMouseEnter={(e) => {
                                                    if (!isLocked && !isConfirmed) {
                                                      e.target.style.background = '#9a5425';
                                                      e.target.style.transform = 'translateY(-1px)';
                                                    }
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    if (!isLocked && !isConfirmed) {
                                                      e.target.style.background = '#b5651d';
                                                      e.target.style.transform = 'translateY(0)';
                                                    }
                                                  }}
                                                >
                                                  + Add Recipient
                                                </button>
                                              )}
                                            </div>

                                            {/* Add Recipient Form */}
                                            {addingRecipient && addingRecipient.orderNo === order.orderNo && addingRecipient.lookId === (look.lookId || look.id) && (
                                              <div className="add-recipient-form" style={{
                                                background: '#fffbf7',
                                                padding: '20px',
                                                borderRadius: '10px',
                                                marginBottom: '20px',
                                                border: '2px solid #b5651d',
                                                boxShadow: '0 2px 6px rgba(181, 101, 29, 0.1)'
                                              }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '15px' }}>
                                                  <span style={{ fontSize: '18px' }}>➕</span>
                                                  <h6 style={{ margin: 0, color: '#b5651d', fontSize: '16px', fontWeight: '600' }}>Add New Recipient</h6>
                                                </div>
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '15px', alignItems: 'end' }}>
                                                  <div>
                                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Name *</label>
                                                    <input
                                                      type="text"
                                                      value={newRecipient.name}
                                                      onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })}
                                                      placeholder="Full Name"
                                                      style={{
                                                        width: '100%',
                                                        padding: '10px 12px',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '8px',
                                                        fontSize: '14px',
                                                        transition: 'border-color 0.2s',
                                                        outline: 'none'
                                                      }}
                                                      onFocus={(e) => e.target.style.borderColor = '#b5651d'}
                                                      onBlur={(e) => e.target.style.borderColor = '#ddd'}
                                                    />
                                                  </div>
                                                  <div>
                                                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: '#495057' }}>Email *</label>
                                                    <input
                                                      type="email"
                                                      value={newRecipient.email}
                                                      onChange={(e) => setNewRecipient({ ...newRecipient, email: e.target.value })}
                                                      placeholder="email@example.com"
                                                      style={{
                                                        width: '100%',
                                                        padding: '10px 12px',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '8px',
                                                        fontSize: '14px',
                                                        transition: 'border-color 0.2s',
                                                        outline: 'none'
                                                      }}
                                                      onFocus={(e) => e.target.style.borderColor = '#b5651d'}
                                                      onBlur={(e) => e.target.style.borderColor = '#ddd'}
                                                    />
                                                  </div>
                                                  <div style={{ display: 'flex', gap: '10px' }}>
                                                    <button
                                                      onClick={() => handleAddRecipient(order.orderNo, look.lookId || look.id)}
                                                      disabled={isLocked || isConfirmed || updatingRecipients[`${order.orderNo}-${look.lookId || look.id}-add`]}
                                                      style={{
                                                        padding: '10px 20px',
                                                        background: (isLocked || isConfirmed) ? '#6c757d' : '#b5651d',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        cursor: (isLocked || isConfirmed) ? 'not-allowed' : 'pointer',
                                                        fontSize: '14px',
                                                        fontWeight: '600',
                                                        opacity: (isLocked || isConfirmed) ? 0.6 : 1,
                                                        transition: 'all 0.2s ease',
                                                        whiteSpace: 'nowrap',
                                                        boxShadow: (isLocked || isConfirmed) ? 'none' : '0 2px 4px rgba(181, 101, 29, 0.2)'
                                                      }}
                                                      onMouseEnter={(e) => {
                                                        if (!isLocked && !isConfirmed) {
                                                          e.target.style.background = '#9a5425';
                                                        }
                                                      }}
                                                      onMouseLeave={(e) => {
                                                        if (!isLocked && !isConfirmed) {
                                                          e.target.style.background = '#b5651d';
                                                        }
                                                      }}
                                                    >
                                                      {updatingRecipients[`${order.orderNo}-${look.lookId || look.id}-add`] ? 'Adding...' : 'Add'}
                                                    </button>
                                                    <button
                                                      onClick={() => {
                                                        setAddingRecipient(null);
                                                        setNewRecipient({ name: "", email: "" });
                                                      }}
                                                      style={{
                                                        padding: '10px 20px',
                                                        background: '#6c757d',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer',
                                                        fontSize: '14px',
                                                        fontWeight: '600',
                                                        transition: 'all 0.2s ease',
                                                        whiteSpace: 'nowrap'
                                                      }}
                                                      onMouseEnter={(e) => {
                                                        e.target.style.background = '#5a6268';
                                                      }}
                                                      onMouseLeave={(e) => {
                                                        e.target.style.background = '#6c757d';
                                                      }}
                                                    >
                                                      Cancel
                                                    </button>
                                                  </div>
                                                </div>
                                              </div>
                                            )}

                                            {/* Recipients List */}
                                            <div className="recipients-list">
                                              {look.recipients && look.recipients.map((recipient, recIdx) => {
                                                const isEditingEmail = editingRecipient && 
                                                  editingRecipient.orderNo === order.orderNo && 
                                                  editingRecipient.lookId === (look.lookId || look.id) &&
                                                  editingRecipient.recipientIndex === recIdx &&
                                                  editingRecipient.field === 'email';
                                                
                                                const isEditingName = editingRecipient && 
                                                  editingRecipient.orderNo === order.orderNo && 
                                                  editingRecipient.lookId === (look.lookId || look.id) &&
                                                  editingRecipient.recipientIndex === recIdx &&
                                                  editingRecipient.field === 'name';

                                                const updateKey = `${order.orderNo}-${look.lookId || look.id}-${recIdx}`;
                                                
                                                return (
                                                  <div
                                                    key={recipient.recipientId || recIdx}
                                                    className={`recipient-status-item ${recipient.status === 'SUBMITTED' ? 'submitted' : 'pending'}`}
                                                    style={{
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      padding: '16px',
                                                      marginBottom: '12px',
                                                      background: recipient.status === 'SUBMITTED' ? '#d4edda' : '#fffbf7',
                                                      borderRadius: '12px',
                                                      border: `2px solid ${recipient.status === 'SUBMITTED' ? '#28a745' : '#b5651d'}`,
                                                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                      transition: 'all 0.2s ease'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                      e.currentTarget.style.transform = 'translateY(-2px)';
                                                      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                      e.currentTarget.style.transform = 'translateY(0)';
                                                      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                                                    }}
                                                  >
                                                    <div className="recipient-info" style={{ flex: '1', display: 'flex', width:'100%', flexDirection: 'column', gap: '12px', minWidth: 0, marginBottom: '12px' }}>
                                                      {isEditingName ? (
                                                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
                                                          <input
                                                            type="text"
                                                            value={editingValue}
                                                            onChange={(e) => setEditingValue(e.target.value)}
                                                            onKeyDown={(e) => {
                                                              if (e.key === 'Enter') {
                                                                handleUpdateRecipient(order.orderNo, look.lookId || look.id, recIdx, 'name', editingValue);
                                                              } else if (e.key === 'Escape') {
                                                                setEditingRecipient(null);
                                                                setEditingValue("");
                                                              }
                                                            }}
                                                            autoFocus
                                                            style={{
                                                              padding: '8px 12px',
                                                              border: '2px solid #b5651d',
                                                              borderRadius: '8px',
                                                              fontSize: '14px',
                                                              flex: '1',
                                                              minWidth: '150px',
                                                              outline: 'none',
                                                              transition: 'border-color 0.2s'
                                                            }}
                                                          />
                                                          <button
                                                            onClick={() => handleUpdateRecipient(order.orderNo, look.lookId || look.id, recIdx, 'name', editingValue)}
                                                            disabled={isLocked || isConfirmed || updatingRecipients[updateKey]}
                                                            style={{
                                                              padding: '8px 16px',
                                                              background: (isLocked || isConfirmed) ? '#6c757d' : '#b5651d',
                                                              color: 'white',
                                                              border: 'none',
                                                              borderRadius: '8px',
                                                              cursor: (isLocked || isConfirmed) ? 'not-allowed' : 'pointer',
                                                              fontSize: '13px',
                                                              fontWeight: '600',
                                                              opacity: (isLocked || isConfirmed) ? 0.6 : 1,
                                                              transition: 'all 0.2s ease'
                                                            }}
                                                          >
                                                            {updatingRecipients[updateKey] ? 'Saving...' : 'Save'}
                                                          </button>
                                                          <button
                                                            onClick={() => {
                                                              setEditingRecipient(null);
                                                              setEditingValue("");
                                                            }}
                                                            style={{
                                                              padding: '8px 16px',
                                                              background: '#6c757d',
                                                              color: 'white',
                                                              border: 'none',
                                                              borderRadius: '8px',
                                                              cursor: 'pointer',
                                                              fontSize: '13px',
                                                              fontWeight: '600',
                                                              transition: 'all 0.2s ease'
                                                            }}
                                                          >
                                                            Cancel
                                                          </button>
                                                        </div>
                                                      ) : (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                          <span style={{ fontSize: '18px', flexShrink: 0, color: '#6c5ce7' }}>👤</span>
                                                          <span className="recipient-name" style={{ 
                                                            cursor: (!isLocked && !isConfirmed && recipient.status !== 'SUBMITTED') ? 'pointer' : 'default',
                                                            fontSize: '15px',
                                                            fontWeight: '700',
                                                            color: '#2c3e50',
                                                            flex: '1',
                                                            minWidth: 0,
                                                            wordBreak: 'break-word'
                                                          }}>
                                                            {recipient.name}
                                                          </span>
                                                          {!isLocked && !isConfirmed && (
                                                            <button
                                                              onClick={() => {
                                                                setEditingRecipient({ orderNo: order.orderNo, lookId: look.lookId || look.id, recipientIndex: recIdx, field: 'name' });
                                                                setEditingValue(recipient.name);
                                                              }}
                                                              disabled={isLocked || isConfirmed}
                                                              style={{
                                                                padding: '4px 10px',
                                                                background: '#fffbf7',
                                                                border: '1px solid #b5651d',
                                                                borderRadius: '6px',
                                                                color: '#b5651d',
                                                                cursor: 'pointer',
                                                                fontSize: '12px',
                                                                fontWeight: '500',
                                                                transition: 'all 0.2s ease',
                                                                whiteSpace: 'nowrap',
                                                                boxShadow: '0 1px 2px rgba(181, 101, 29, 0.1)'
                                                              }}
                                                              title="Edit name"
                                                              onMouseEnter={(e) => {
                                                                e.target.style.background = '#b5651d';
                                                                e.target.style.color = 'white';
                                                                e.target.style.transform = 'scale(1.05)';
                                                              }}
                                                              onMouseLeave={(e) => {
                                                                e.target.style.background = '#fffbf7';
                                                                e.target.style.color = '#b5651d';
                                                                e.target.style.transform = 'scale(1)';
                                                              }}
                                                            >
                                                              ✏️ Edit
                                                            </button>
                                                          )}
                                                        </div>
                                                      )}
                                                      
                                                      {isEditingEmail ? (
                                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginTop: '8px' }}>
                                                          <input
                                                            type="email"
                                                            value={editingValue}
                                                            onChange={(e) => setEditingValue(e.target.value)}
                                                            onKeyDown={(e) => {
                                                              if (e.key === 'Enter') {
                                                                handleUpdateRecipient(order.orderNo, look.lookId || look.id, recIdx, 'email', editingValue);
                                                              } else if (e.key === 'Escape') {
                                                                setEditingRecipient(null);
                                                                setEditingValue("");
                                                              }
                                                            }}
                                                            autoFocus
                                                            style={{
                                                              padding: '8px 12px',
                                                              border: '2px solid #b5651d',
                                                              borderRadius: '8px',
                                                              fontSize: '14px',
                                                              flex: '1',
                                                              minWidth: '200px',
                                                              outline: 'none',
                                                              transition: 'border-color 0.2s'
                                                            }}
                                                          />
                                                          <button
                                                            onClick={() => handleUpdateRecipient(order.orderNo, look.lookId || look.id, recIdx, 'email', editingValue)}
                                                            disabled={isLocked || isConfirmed || updatingRecipients[updateKey]}
                                                            style={{
                                                              padding: '8px 16px',
                                                              background: (isLocked || isConfirmed) ? '#6c757d' : '#b5651d',
                                                              color: 'white',
                                                              border: 'none',
                                                              borderRadius: '8px',
                                                              cursor: (isLocked || isConfirmed) ? 'not-allowed' : 'pointer',
                                                              fontSize: '13px',
                                                              fontWeight: '600',
                                                              opacity: (isLocked || isConfirmed) ? 0.6 : 1,
                                                              transition: 'all 0.2s ease'
                                                            }}
                                                          >
                                                            {updatingRecipients[updateKey] ? 'Saving...' : 'Save'}
                                                          </button>
                                                          <button
                                                            onClick={() => {
                                                              setEditingRecipient(null);
                                                              setEditingValue("");
                                                            }}
                                                            style={{
                                                              padding: '8px 16px',
                                                              background: '#6c757d',
                                                              color: 'white',
                                                              border: 'none',
                                                              borderRadius: '8px',
                                                              cursor: 'pointer',
                                                              fontSize: '13px',
                                                              fontWeight: '600',
                                                              transition: 'all 0.2s ease'
                                                            }}
                                                          >
                                                            Cancel
                                                          </button>
                                                        </div>
                                                      ) : (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                          <span style={{ fontSize: '18px', flexShrink: 0, color: '#6c5ce7' }}>📧</span>
                                                          <span className="recipient-email" style={{ 
                                                            cursor: (!isLocked && !isConfirmed && recipient.status !== 'SUBMITTED') ? 'pointer' : 'default',
                                                            fontSize: '14px',
                                                            color: '#2c3e50',
                                                            flex: '1',
                                                            minWidth: 0,
                                                            wordBreak: 'break-word'
                                                          }}>
                                                            {recipient.email}
                                                          </span>
                                                          {!isLocked && !isConfirmed && (
                                                            <button
                                                              onClick={() => {
                                                                setEditingRecipient({ orderNo: order.orderNo, lookId: look.lookId || look.id, recipientIndex: recIdx, field: 'email' });
                                                                setEditingValue(recipient.email);
                                                              }}
                                                              disabled={isLocked || isConfirmed}
                                                              style={{
                                                                padding: '4px 10px',
                                                                background: '#fffbf7',
                                                                border: '1px solid #b5651d',
                                                                borderRadius: '6px',
                                                                color: '#b5651d',
                                                                cursor: 'pointer',
                                                                fontSize: '12px',
                                                                fontWeight: '500',
                                                                transition: 'all 0.2s ease',
                                                                whiteSpace: 'nowrap',
                                                                boxShadow: '0 1px 2px rgba(181, 101, 29, 0.1)'
                                                              }}
                                                              title="Edit email"
                                                              onMouseEnter={(e) => {
                                                                e.target.style.background = '#b5651d';
                                                                e.target.style.color = 'white';
                                                                e.target.style.transform = 'scale(1.05)';
                                                              }}
                                                              onMouseLeave={(e) => {
                                                                e.target.style.background = '#fffbf7';
                                                                e.target.style.color = '#b5651d';
                                                                e.target.style.transform = 'scale(1)';
                                                              }}
                                                            >
                                                              ✏️ Edit
                                                            </button>
                                                          )}
                                                        </div>
                                                      )}
                                                    </div>
                                                    
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginTop: '8px', paddingTop: '12px', borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                                                      {!isLocked && !isConfirmed && (
                                                        <button
                                                          onClick={() => handleDeleteRecipient(order.orderNo, look.lookId || look.id, recIdx)}
                                                          disabled={isLocked || isConfirmed || updatingRecipients[updateKey]}
                                                          style={{
                                                            padding: '8px 16px',
                                                            background: (isLocked || isConfirmed) ? '#6c757d' : '#dc3545',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            cursor: (isLocked || isConfirmed) ? 'not-allowed' : 'pointer',
                                                            fontSize: '13px',
                                                            fontWeight: '600',
                                                            opacity: (isLocked || isConfirmed) ? 0.6 : 1,
                                                            transition: 'all 0.2s ease',
                                                            boxShadow: (isLocked || isConfirmed) ? 'none' : '0 2px 4px rgba(220, 53, 69, 0.2)',
                                                            whiteSpace: 'nowrap',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '6px'
                                                          }}
                                                          title={(isLocked || isConfirmed) ? "Order confirmed - cannot remove" : "Remove recipient"}
                                                          onMouseEnter={(e) => {
                                                            if (!isLocked && !isConfirmed) {
                                                              e.target.style.background = '#c82333';
                                                              e.target.style.transform = 'translateY(-1px)';
                                                            }
                                                          }}
                                                          onMouseLeave={(e) => {
                                                            if (!isLocked && !isConfirmed) {
                                                              e.target.style.background = '#dc3545';
                                                              e.target.style.transform = 'translateY(0)';
                                                            }
                                                          }}
                                                        >
                                                          🗑️ Remove
                                                        </button>
                                                      )}

                                                      <div className="status-badge-wrapper" style={{ flexShrink: 0, marginLeft: 'auto' }}>
                                                        {recipient.status === 'SUBMITTED' ? (
                                                          <div className="status-badge submitted-badge" style={{
                                                            padding: '10px 16px',
                                                            background: '#28a745',
                                                            color: 'white',
                                                            borderRadius: '8px',
                                                            fontSize: '13px',
                                                            fontWeight: '700',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            gap: '4px',
                                                            boxShadow: '0 2px 4px rgba(40, 167, 69, 0.3)',
                                                            minWidth: '120px'
                                                          }}>
                                                            <span>✓ SUBMITTED</span>
                                                            {recipient.submittedAt && (
                                                              <span className="submission-date" style={{ fontSize: '11px', opacity: 0.95, fontWeight: '500' }}>
                                                                {formatDate(recipient.submittedAt)}
                                                              </span>
                                                            )}
                                                          </div>
                                                        ) : (
                                                          <span className="status-badge pending-badge" style={{
                                                            padding: '6px 14px',
                                                            background: '#b5651d',
                                                            color: 'white',
                                                            borderRadius: '8px',
                                                            fontSize: '12px',
                                                            fontWeight: '600',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '6px',
                                                            boxShadow: '0 2px 4px rgba(181, 101, 29, 0.3)'
                                                          }}>
                                                            ⏳ Pending
                                                          </span>
                                                        )}
                                                      </div>
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {/* Submitted Measurements Section */}
                                    {(() => {
                                      // Get all submissions for this order (for fallback data)
                                      const orderSubmissions = submissions.filter(sub => {
                                        const subOrderNo = sub.order_no || sub.orderNo;
                                        const orderNoStr = String(order.orderNo || '').trim();
                                        const subOrderNoStr = String(subOrderNo || '').trim();
                                        return subOrderNoStr === orderNoStr || 
                                               parseInt(subOrderNoStr) === parseInt(orderNoStr);
                                      });

                                      // Get all recipients from all looks in this order
                                      const allRecipients = [];
                                      if (order.looks && order.looks.length > 0) {
                                        order.looks.forEach(look => {
                                          if (look.recipients && look.recipients.length > 0) {
                                            look.recipients.forEach(recipient => {
                                              // Check if recipient has submitted (use status from API or match from submissions)
                                              const recipientStatus = recipient.status || 'PENDING';
                                              const isSubmitted = recipientStatus === 'SUBMITTED';
                                              
                                              // Find submission for this recipient (for additional data)
                                              const recipientSubmission = orderSubmissions.find(sub => {
                                                const subEmail = (sub.recipient_email || sub.recipientEmail || '').toLowerCase().trim();
                                                const recipientEmail = (recipient.email || '').toLowerCase().trim();
                                                return subEmail === recipientEmail && subEmail !== '';
                                              });

                                              // Use measurements from recipient object (from API) or from submissions array
                                              const measurements = recipient.measurements || recipientSubmission?.measurements || null;
                                              
                                              // Parse measurements if it's a string
                                              let parsedMeasurements = measurements;
                                              if (typeof measurements === 'string') {
                                                try {
                                                  parsedMeasurements = JSON.parse(measurements);
                                                } catch (e) {
                                                  parsedMeasurements = measurements;
                                                }
                                              }

                                              allRecipients.push({
                                                name: recipient.name || recipient.email || 'Unknown',
                                                email: recipient.email || 'N/A',
                                                lookName: look.lookName || look.look_name || 'N/A',
                                                lookTitle: look.lookTitle || look.look_title || '',
                                                submission: recipientSubmission || (isSubmitted ? {
                                                  measurements: parsedMeasurements,
                                                  submitted_at: recipient.submittedAt || recipient.submitted_at
                                                } : null),
                                                submitted: isSubmitted || !!recipientSubmission,
                                                measurements: parsedMeasurements
                                              });
                                            });
                                          }
                                        });
                                      }

                                      if (allRecipients.length === 0) return null;

                                      return (
                                        <div className="submitted-measurements-section" style={{
                                          marginTop: '30px',
                                          padding: '20px',
                                          background: '#f8f9fa',
                                          borderRadius: '12px',
                                          border: '1px solid #e9ecef'
                                        }}>
                                          <h4 style={{
                                            margin: '0 0 20px 0',
                                            color: '#b5651d',
                                            fontSize: '18px',
                                            fontWeight: '600',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px'
                                          }}>
                                            <FaBox />
                                            Submitted Measurements ({allRecipients.filter(r => r.submitted).length}/{allRecipients.length})
                                          </h4>
                                          
                                          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                            {allRecipients.map((recipient, idx) => {
                                              const recipientKey = `${order.orderNo}-${recipient.email}-${idx}`;
                                              const isExpanded = expandedRecipientMeasurements.has(recipientKey);
                                              const hasMeasurements = recipient.submitted && recipient.measurements;
                                              
                                              return (
                                                <div key={idx} style={{
                                                  background: recipient.submitted ? '#d4edda' : '#fff3cd',
                                                  border: `2px solid ${recipient.submitted ? '#28a745' : '#ffc107'}`,
                                                  borderRadius: '10px',
                                                  padding: '16px',
                                                  transition: 'all 0.3s ease'
                                                }}>
                                                  <div 
                                                    style={{
                                                      display: 'flex',
                                                      justifyContent: 'space-between',
                                                      alignItems: 'center',
                                                      marginBottom: hasMeasurements ? '12px' : '0',
                                                      flexWrap: 'wrap',
                                                      gap: '8px',
                                                      cursor: hasMeasurements ? 'pointer' : 'default'
                                                    }}
                                                    onClick={() => {
                                                      if (hasMeasurements) {
                                                        setExpandedRecipientMeasurements(prev => {
                                                          const newSet = new Set(prev);
                                                          if (newSet.has(recipientKey)) {
                                                            newSet.delete(recipientKey);
                                                          } else {
                                                            newSet.add(recipientKey);
                                                          }
                                                          return newSet;
                                                        });
                                                      }
                                                    }}
                                                  >
                                                    <div style={{ flex: 1 }}>
                                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <strong style={{ color: '#2c3e50', fontSize: '15px' }}>
                                                          {recipient.name}
                                                        </strong>
                                                        {hasMeasurements && (
                                                          <span style={{ color: '#6c757d', fontSize: '14px' }}>
                                                            {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                                                          </span>
                                                        )}
                                                      </div>
                                                      <div style={{ fontSize: '13px', color: '#6c757d', marginTop: '4px' }}>
                                                        {recipient.email}
                                                      </div>
                                                      <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                                                        Look: {recipient.lookName} {recipient.lookTitle ? `- ${recipient.lookTitle}` : ''}
                                                      </div>
                                                    </div>
                                                    <div style={{
                                                      padding: '6px 12px',
                                                      borderRadius: '20px',
                                                      fontSize: '12px',
                                                      fontWeight: '600',
                                                      background: recipient.submitted ? '#28a745' : '#ffc107',
                                                      color: 'white'
                                                    }}>
                                                      {recipient.submitted ? '✓ Submitted' : '⏳ Pending'}
                                                    </div>
                                                  </div>

                                                  {hasMeasurements && (
                                                    <div 
                                                      style={{
                                                        maxHeight: isExpanded ? '1000px' : '0',
                                                        overflow: 'hidden',
                                                        transition: 'max-height 0.3s ease, opacity 0.3s ease',
                                                        opacity: isExpanded ? 1 : 0,
                                                        marginTop: isExpanded ? '12px' : '0'
                                                      }}
                                                    >
                                                      <div style={{
                                                        padding: '12px',
                                                        background: 'white',
                                                        borderRadius: '8px',
                                                        border: '1px solid #dee2e6'
                                                      }}>
                                                        <div style={{
                                                          display: 'grid',
                                                          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                                                          gap: '10px',
                                                          fontSize: '13px'
                                                        }}>
                                                          {Array.isArray(recipient.measurements) ? (
                                                            recipient.measurements.map((measurement, mIdx) => (
                                                              <div key={mIdx} style={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                padding: '8px',
                                                                background: '#f8f9fa',
                                                                borderRadius: '6px'
                                                              }}>
                                                                <span style={{ fontWeight: '600', color: '#495057' }}>
                                                                  {measurement.label || measurement.name || 'Measurement'}:
                                                                </span>
                                                                <span style={{ color: '#b5651d', fontWeight: '600' }}>
                                                                  {measurement.value} inches
                                                                </span>
                                                              </div>
                                                            ))
                                                          ) : typeof recipient.measurements === 'object' && recipient.measurements !== null ? (
                                                            Object.entries(recipient.measurements).map(([key, value]) => (
                                                              <div key={key} style={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                padding: '8px',
                                                                background: '#f8f9fa',
                                                                borderRadius: '6px'
                                                              }}>
                                                                <span style={{ fontWeight: '600', color: '#495057', textTransform: 'capitalize' }}>
                                                                  {key.replace(/_/g, ' ')}:
                                                                </span>
                                                                <span style={{ color: '#b5651d', fontWeight: '600' }}>
                                                                  {value} inches
                                                                </span>
                                                              </div>
                                                            ))
                                                          ) : (
                                                            <p style={{ color: '#6c757d', fontSize: '12px' }}>No measurements data available</p>
                                                          )}
                                                        </div>
                                                        {(recipient.submission?.submitted_at || recipient.submittedAt) && (
                                                          <div style={{
                                                            marginTop: '10px',
                                                            fontSize: '11px',
                                                            color: '#6c757d',
                                                            textAlign: 'right'
                                                          }}>
                                                            Submitted: {formatDate(recipient.submission?.submitted_at || recipient.submittedAt)}
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  )}

                                                  {!recipient.submitted && (
                                                    <div style={{
                                                      marginTop: '12px',
                                                      padding: '12px',
                                                      background: 'white',
                                                      borderRadius: '8px',
                                                      border: '1px dashed #ffc107',
                                                      textAlign: 'center',
                                                      color: '#856404',
                                                      fontSize: '13px'
                                                    }}>
                                                      ⏳ Waiting for measurement submission
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      );
                                    })()}
                                  </div>

                                  {/* Right Side: Billing Information - Only show when order is NOT locked and NOT confirmed */}
                                  {!isLocked && !isConfirmed && (
                                  <div className="billing-info-right">
                                    <h4>Billing Information</h4>
                                    
                                    {/* Pricing Summary */}
                                    {order.looks && order.looks.length > 0 && (() => {
                                      // Get lock status for this order
                                      const lockStatus = orderLockStatus[order.orderNo] || {};
                                      const isLocked = lockStatus.isLocked;
                                      const orderExistsInOrdersTable = lockStatus.order !== undefined && lockStatus.order !== null;
                                      const isConfirmed = orderExistsInOrdersTable || lockStatus.status?.confirmed === true;
                                      
                                      // Use billing country if available, otherwise use selected country, otherwise default to United States
                                      const billingCountry = billingInfo[order.orderNo]?.country;
                                      const country = billingCountry || selectedCountry[order.orderNo] || "United States";
                                      // Sync selectedCountry with billing country if billing country exists
                                      if (billingCountry && selectedCountry[order.orderNo] !== billingCountry) {
                                        setSelectedCountry(prev => ({
                                          ...prev,
                                          [order.orderNo]: billingCountry
                                        }));
                                      }
                                      const pricing = calculateOrderPricing(order, order.orderNo);
                                      
                                      return (
                                        <div className="order-pricing-summary">
                                          {/* Country Selection */}
                                          <div className="pricing-country-select">
                                            <label htmlFor={`country-select-${order.orderNo}`}>
                                              <strong>Country of Origin *</strong>
                                            </label>
                                            <select
                                              id={`country-select-${order.orderNo}`}
                                              value={country}
                                              onChange={(e) => {
                                                const selectedCountryValue = e.target.value;
                                                setSelectedCountry(prev => ({
                                                  ...prev,
                                                  [order.orderNo]: selectedCountryValue
                                                }));
                                                // Also update billing country to keep them in sync
                                                if (!isLocked && !isConfirmed) {
                                                  handleBillingCountryChange(order.orderNo, selectedCountryValue);
                                                }
                                              }}
                                              className="country-select"
                                              disabled={isLocked || isConfirmed}
                                              readOnly={isLocked || isConfirmed}
                                              style={{
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: '8px',
                                                border: '1px solid #ddd',
                                                fontSize: '14px',
                                                marginTop: '8px'
                                              }}
                                            >
                                              <option value="">Select Country</option>
                                              {countries.map((c, index) => (
                                                <option key={`${c.name}-${index}`} value={c.name}>
                                                  {c.name}
                                                </option>
                                              ))}
                                            </select>
                                            <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                              Currency: {pricing.currency.code} | Tax: {(FIXED_TAX_RATE * 100).toFixed(0)}%
                                            </p>
                                          </div>
                                          
                                          <div className="pricing-row">
                                            <span>Price per Outfit:</span>
                                            <span>{formatCurrency(pricing.pricePerOutfit, pricing.currency.code)} {pricing.currency.code}</span>
                                          </div>
                                          <div className="pricing-row">
                                            <span>Total Quantity:</span>
                                            <span>{order.totalQuantity || 0}</span>
                                          </div>
                                          <div className="pricing-row pricing-subtotal">
                                            <span>Subtotal:</span>
                                            <span>{formatCurrency(pricing.subtotal, pricing.currency.code)} {pricing.currency.code}</span>
                                          </div>
                                          <div className="pricing-row">
                                            <span>Tax ({(FIXED_TAX_RATE * 100).toFixed(0)}%):</span>
                                            <span>{formatCurrency(pricing.taxAmount, pricing.currency.code)} {pricing.currency.code}</span>
                                          </div>
                                          <div className="pricing-row pricing-shipping">
                                            <span>Shipping Charges:</span>
                                            <span>{formatCurrency(pricing.shippingCharges, pricing.currency.code)} {pricing.currency.code}</span>
                                          </div>
                                          <div className="pricing-row pricing-total">
                                            <span><strong>Total Amount:</strong></span>
                                            <span><strong>{formatCurrency(pricing.total, pricing.currency.code)} {pricing.currency.code}</strong></span>
                                          </div>
                                     
                                        </div>
                                      );
                                    })()}
                                    
                                    {/* Bank Account Details */}
                                    <div className="bank-details-section">
                                      <h5>Payment Details</h5>
                                      <div className="bank-info">
                                          <div className="bank-note">
                                            <div className="bank-note-header">
                                              <FaInfoCircle className="bank-note-icon" />
                                              <h6>Complete Your Payment</h6>
                                            </div>
                                            <div className="bank-note-content">
                                              <p>To complete your order, please submit an e-transfer to:</p>
                                              <div className="bank-email">
                                                <FaEnvelope className="email-icon" />
                                                <span className="bank-email-text">
                                                  <strong>info@shawlhaus.com</strong>
                                                </span>
                                              </div>
                                              <p className="bank-note-footer">Once the e-transfer has been completed, kindly upload proof of payment in the box below.</p>
                                            </div>
                                          </div>
                                      </div>
                                      
                                      {/* Payment Proof Upload Section */}
                                      <div className="payment-proof-upload-section">
                                        <h5>Upload Payment Proof</h5>
                                        {orderPaymentProof ? (
                                          <div className="payment-proof-uploaded">
                                            <div className="proof-status">
                                              <FaFileUpload className="proof-icon" />
                                              <div>
                                                <p><strong>Proof Uploaded Successfully</strong></p>
                                                <a 
                                                  href={`${base_url.replace("/apis", "")}${orderPaymentProof}`} 
                                                  target="_blank" 
                                                  rel="noopener noreferrer"
                                                  className="view-proof-link"
                                                >
                                                  View Proof
                                                </a>
                                                <p className="upload-date">
                                                  Uploaded on: {orderPaymentProofUploadedAt ? formatDate(orderPaymentProofUploadedAt) : 'N/A'}
                                                </p>
                                              </div>
                                            </div>
                                              {!isConfirmed && (
                                              <button
                                                type="button"
                                                className="btn-secondary upload-new-proof-btn"
                                                onClick={() => {
                                                  const fileInput = document.getElementById(`payment-proof-input-${orderId}`);
                                                  if (fileInput) fileInput.click();
                                                }}
                                              >
                                                <FaUpload /> Upload New Proof
                                              </button>
                                            )}
                                          </div>
                                        ) : (
                                          <div className="payment-proof-upload-form">
                                            <label htmlFor={`payment-proof-input-${orderId}`} className="upload-label">
                                              <FaUpload className="upload-icon" />
                                              <span>Select Payment Proof File</span>
                                              <small>JPEG, PNG, or PDF (Max 5MB)</small>
                                            </label>
                                            <input
                                              type="file"
                                              id={`payment-proof-input-${orderId}`}
                                              accept="image/jpeg,image/jpg,image/png,application/pdf"
                                              onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                  handlePaymentProofUpload(order.orderNo, file);
                                                }
                                              }}
                                              style={{ display: 'none' }}
                                                disabled={isLocked || isConfirmed}
                                            />
                                              {!isLocked && !isConfirmed && (
                                              <button
                                                type="button"
                                                className="btn-primary upload-proof-btn"
                                                onClick={() => {
                                                  const fileInput = document.getElementById(`payment-proof-input-${orderId}`);
                                                  if (fileInput) fileInput.click();
                                                }}
                                                disabled={uploadingPaymentProof[order.orderNo]}
                                              >
                                                {uploadingPaymentProof[order.orderNo] ? (
                                                  <>Uploading...</>
                                                ) : (
                                                  <>
                                                    <FaUpload /> Upload Proof
                                                  </>
                                                )}
                                              </button>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Billing Form */}
                                    <div className="billing-form">
                                      {(() => {
                                        // Get the same country value used in Country of Origin dropdown
                                        const billingCountry = billingInfo[order.orderNo]?.country;
                                        const countryForBilling = billingCountry || selectedCountry[order.orderNo] || "United States";
                                        // Sync billingInfo with selectedCountry if they differ
                                        if (selectedCountry[order.orderNo] && selectedCountry[order.orderNo] !== billingCountry && !isLocked && !isConfirmed) {
                                          handleBillingInfoChange(order.orderNo, 'country', selectedCountry[order.orderNo]);
                                        }
                                        
                                        return (
                                      <div className="form-group">
                                        <label htmlFor={`country-${orderId}`}>Country <span className="required-asterisk">*</span></label>
                                        <select
                                          id={`country-${orderId}`}
                                              value={countryForBilling}
                                            onChange={(e) => !isLocked && !isConfirmed && handleBillingCountryChange(order.orderNo, e.target.value)}
                                          className={billingErrors[order.orderNo]?.country ? "error-input" : ""}
                                          required
                                            disabled={isLocked || isConfirmed}
                                            readOnly={isLocked || isConfirmed}
                                        >
                                          <option value="">Select Country</option>
                                          {countries.map((c, index) => (
                                            <option key={`${c.name}-${index}`} value={c.name}>
                                              {c.name}
                                            </option>
                                          ))}
                                        </select>
                                        {billingErrors[order.orderNo]?.country && (
                                          <span className="error-message">{billingErrors[order.orderNo].country}</span>
                                        )}
                                      </div>
                                        );
                                      })()}

                                      <div className="form-group">
                                        <label htmlFor={`state-${orderId}`}>State</label>
                                        <select
                                          id={`state-${orderId}`}
                                          value={billingInfo[order.orderNo]?.state || ''}
                                            onChange={(e) => !isLocked && !isConfirmed && handleBillingStateChange(order.orderNo, e.target.value)}
                                          className={billingErrors[order.orderNo]?.state ? "error-input" : ""}
                                            disabled={isLocked || isConfirmed || !billingInfo[order.orderNo]?.country}
                                            readOnly={isLocked || isConfirmed}
                                        >
                                          <option value="">Select State</option>
                                          {(states[order.orderNo] || []).map((s) => (
                                            <option key={s.name} value={s.name}>
                                              {s.name}
                                            </option>
                                          ))}
                                        </select>
                                        {billingErrors[order.orderNo]?.state && (
                                          <span className="error-message">{billingErrors[order.orderNo].state}</span>
                                        )}
                                      </div>

                                      <div className="form-group">
                                        <label htmlFor={`city-${orderId}`}>City</label>
                                        <select
                                          id={`city-${orderId}`}
                                          value={billingInfo[order.orderNo]?.city || ''}
                                            onChange={(e) => !isLocked && !isConfirmed && handleBillingInfoChange(order.orderNo, 'city', e.target.value)}
                                          className={billingErrors[order.orderNo]?.city ? "error-input" : ""}
                                            disabled={isLocked || isConfirmed}
                                            readOnly={isLocked || isConfirmed}
                                        >
                                          <option value="">Select City</option>
                                          {(cities[order.orderNo] || []).map((city) => (
                                            <option key={city} value={city}>
                                              {city}
                                            </option>
                                          ))}
                                        </select>
                                        {billingErrors[order.orderNo]?.city && (
                                          <span className="error-message">{billingErrors[order.orderNo].city}</span>
                                        )}
                                      </div>

                                      <div className="form-group">
                                        <label htmlFor={`houseNo-${orderId}`}>House/Flat Number <span className="required-asterisk">*</span></label>
                                        <input
                                          type="text"
                                          id={`houseNo-${orderId}`}
                                          value={billingInfo[order.orderNo]?.houseNo || ''}
                                            onChange={(e) => !isLocked && !isConfirmed && handleBillingInfoChange(order.orderNo, 'houseNo', e.target.value)}
                                          placeholder="House/Flat Number"
                                          className={billingErrors[order.orderNo]?.houseNo ? "error-input" : ""}
                                          required
                                            disabled={isLocked || isConfirmed}
                                            readOnly={isLocked || isConfirmed}
                                        />
                                        {billingErrors[order.orderNo]?.houseNo && (
                                          <span className="error-message">{billingErrors[order.orderNo].houseNo}</span>
                                        )}
                                      </div>

                                      <div className="form-group">
                                        <label htmlFor={`street-${orderId}`}>Street Address <span className="required-asterisk">*</span></label>
                                        <input
                                          type="text"
                                          id={`street-${orderId}`}
                                          value={billingInfo[order.orderNo]?.street || ''}
                                            onChange={(e) => !isLocked && !isConfirmed && handleBillingInfoChange(order.orderNo, 'street', e.target.value)}
                                          placeholder="Street Address"
                                          className={billingErrors[order.orderNo]?.street ? "error-input" : ""}
                                          required
                                            disabled={isLocked || isConfirmed}
                                            readOnly={isLocked || isConfirmed}
                                        />
                                        {billingErrors[order.orderNo]?.street && (
                                          <span className="error-message">{billingErrors[order.orderNo].street}</span>
                                        )}
                                      </div>

                                      <div className="form-group">
                                        <label htmlFor={`contact-${orderId}`}>Contact Phone <span className="required-asterisk">*</span></label>
                                        <input
                                          type="tel"
                                          id={`contact-${orderId}`}
                                          value={billingInfo[order.orderNo]?.contact || loggedInUser?.phone || ''}
                                            onChange={(e) => !isLocked && !isConfirmed && handleBillingInfoChange(order.orderNo, 'contact', e.target.value)}
                                          placeholder={loggedInUser?.phone || "Phone Number"}
                                          className={billingErrors[order.orderNo]?.contact ? "error-input" : ""}
                                          required
                                          pattern="[0-9]{10,15}"
                                          title="Phone number must be 10-15 digits"
                                            disabled={isLocked || isConfirmed}
                                            readOnly={isLocked || isConfirmed}
                                        />
                                        {billingErrors[order.orderNo]?.contact && (
                                          <span className="error-message">{billingErrors[order.orderNo].contact}</span>
                                        )}
                                      </div>

                                      <button
                                        type="button"
                                        className="btn-primary confirm-order-btn"
                                        onClick={() => handleConfirmOrder(order)}
                                          disabled={confirmingOrder[order.orderNo] || isLocked || isConfirmed}
                                      >
                                          {confirmingOrder[order.orderNo] ? "Confirming..." : isConfirmed ? "Order Confirmed" : "Confirm Order"}
                                      </button>
                                    </div>
                                  </div>
                                  )}
                                    
                                  {/* Show confirmed order details in read-only format - Complete address and order info - Only show when order IS confirmed */}
                                  {isConfirmed && (() => {
                                    // Try to get confirmed order details from orders state first
                                    let confirmedOrderDetails = getConfirmedOrderDetails(order.orderNo);

                                    // Fallback: if not found in orders state, use address details from lockStatus (orders table)
                                    if (!confirmedOrderDetails && lockStatus && lockStatus.order && lockStatus.order.address) {
                                      const addr = lockStatus.order.address;
                                      confirmedOrderDetails = {
                                        houseNo: addr.houseNo || addr.house_no,
                                        street: addr.street,
                                        city: addr.city,
                                        state: addr.state,
                                        country: addr.country,
                                        contact: addr.contact
                                      };
                                    }

                                    // If we still don't have any details, don't show a loading message – just skip the block
                                      if (!confirmedOrderDetails) {
                                      return null;
                                    }

                                    // Calculate pricing for the confirmed order
                                    // Use country from confirmedOrderDetails or fallback to billingInfo
                                    const orderCountry = confirmedOrderDetails.country || billingInfo[order.orderNo]?.country || selectedCountry[order.orderNo] || "Canada";
                                    const pricing = calculateOrderPricing(order, order.orderNo);
                                      
                                      return (
                                        <div className="billing-info-right">
                                          <div className="confirmed-order-details" style={{
                                            background: '#f8f9fa',
                                            padding: '20px',
                                            borderRadius: '8px',
                                            border: '1px solid #dee2e6'
                                          }}>
                                          <h5 style={{ marginTop: 0, color: '#495057', marginBottom: '20px', fontSize: '18px' }}>✅ Confirmed Order Details</h5>
                                          
                                          {/* Complete Address Section */}
                                          <div style={{ 
                                            background: 'white', 
                                            padding: '15px', 
                                            borderRadius: '6px', 
                                            marginBottom: '20px',
                                            border: '1px solid #e9ecef'
                                          }}>
                                            <h6 style={{ marginTop: 0, marginBottom: '15px', color: '#495057', fontSize: '16px' }}>📍 Delivery Address</h6>
                                            <div style={{ display: 'grid', gap: '12px' }}>
                                              <div>
                                                <label style={{ fontWeight: 'bold', color: '#6c757d', display: 'block', marginBottom: '5px', fontSize: '13px' }}>House/Flat Number:</label>
                                                <p style={{ margin: 0, color: '#212529', fontSize: '14px' }}>{confirmedOrderDetails.houseNo || 'N/A'}</p>
                                              </div>
                                              {confirmedOrderDetails.street && (
                                                <div>
                                                  <label style={{ fontWeight: 'bold', color: '#6c757d', display: 'block', marginBottom: '5px', fontSize: '13px' }}>Street Address:</label>
                                                  <p style={{ margin: 0, color: '#212529', fontSize: '14px' }}>{confirmedOrderDetails.street}</p>
                                                </div>
                                              )}
                                              <div>
                                                <label style={{ fontWeight: 'bold', color: '#6c757d', display: 'block', marginBottom: '5px', fontSize: '13px' }}>City:</label>
                                                <p style={{ margin: 0, color: '#212529', fontSize: '14px' }}>{confirmedOrderDetails.city || 'N/A'}</p>
                                              </div>
                                              <div>
                                                <label style={{ fontWeight: 'bold', color: '#6c757d', display: 'block', marginBottom: '5px', fontSize: '13px' }}>State:</label>
                                                <p style={{ margin: 0, color: '#212529', fontSize: '14px' }}>{confirmedOrderDetails.state || 'N/A'}</p>
                                              </div>
                                              <div>
                                                <label style={{ fontWeight: 'bold', color: '#6c757d', display: 'block', marginBottom: '5px', fontSize: '13px' }}>Country:</label>
                                                <p style={{ margin: 0, color: '#212529', fontSize: '14px' }}>{confirmedOrderDetails.country || 'N/A'}</p>
                                              </div>
                                              <div>
                                                <label style={{ fontWeight: 'bold', color: '#6c757d', display: 'block', marginBottom: '5px', fontSize: '13px' }}>Contact Phone:</label>
                                                <p style={{ margin: 0, color: '#212529', fontSize: '14px' }}>{confirmedOrderDetails.contact || 'N/A'}</p>
                                              </div>
                                            </div>
                                          </div>
                                          
                                          {/* Order Summary */}
                                          <div style={{ 
                                            background: 'white', 
                                            padding: '15px', 
                                            borderRadius: '6px',
                                            border: '1px solid #e9ecef'
                                          }}>
                                            <h6 style={{ marginTop: 0, marginBottom: '15px', color: '#495057', fontSize: '16px' }}>💰 Order Summary</h6>
                                            <div>
                                              <label style={{ fontWeight: 'bold', color: '#6c757d', display: 'block', marginBottom: '5px', fontSize: '13px' }}>Total Amount:</label>
                                              <p style={{ margin: 0, color: '#28a745', fontSize: '20px', fontWeight: 'bold' }}>{formatCurrency(pricing.total, pricing.currency.code)} {pricing.currency.code}</p>
                                            </div>
                                          </div>
                                          
                                          {/* Payment Proof if available */}
                                          {orderPaymentProof && (
                                            <div style={{ 
                                              background: 'white', 
                                              padding: '15px', 
                                              borderRadius: '6px',
                                              marginTop: '15px',
                                              border: '1px solid #e9ecef'
                                            }}>
                                              <h6 style={{ marginTop: 0, marginBottom: '10px', color: '#495057', fontSize: '16px' }}>📄 Payment Proof</h6>
                                              <a 
                                                href={`${base_url.replace("/apis", "")}${orderPaymentProof}`} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                style={{
                                                  color: '#007bff',
                                                  textDecoration: 'none',
                                                  fontSize: '14px'
                                                }}
                                              >
                                                View Payment Proof →
                                              </a>
                                            </div>
                                          )}
                                          
                                          <div style={{
                                            marginTop: '20px',
                                            padding: '15px',
                                            background: '#fff3cd',
                                            border: '2px solid #ffc107',
                                            borderRadius: '6px',
                                            textAlign: 'center'
                                          }}>
                                            <p style={{ margin: 0, color: '#856404', fontWeight: 'bold', fontSize: '14px' }}>
                                              ⚠️ Order Confirmed - Not able to modify
                                            </p>
                                          </div>
                                        </div>
                                          </div>
                                      );
                                    })()}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Measurements Tab */}
      {activeTab === "measurements" && (
        <div className="tab-content">
          <div className="measurements-tab-content">
            {/* Admin Orders Section */}
            {loggedInUser && (
              <div className="admin-orders-section">
                <h2>Measurement Requests (As Admin)</h2>
                {measurementOrders.filter(order => !areAllRecipientsSubmitted(order)).length === 0 ? (
                  <div className="no-orders">
                    <p>You haven't created any measurement requests yet.</p>
                    <button
                      onClick={() => navigate("/customization")}
                      className="btn-primary"
                    >
                      Create Measurement Request
                    </button>
                  </div>
                ) : (
                  <div className="measurement-orders-grid">
                    {measurementOrders
                      .filter(order => !areAllRecipientsSubmitted(order)) // Hide orders where all recipients have submitted
                      .map((order, idx) => {
                      // Debug: log order to see what we're getting
                      if (!order.orderNo) {
                      }
                      const orderId = order.orderNo || idx;
                      const isExpanded = expandedMeasurementOrders.has(orderId);
                      const allSubmitted = areAllRecipientsSubmitted(order);
                      const lockStatus = orderLockStatus[order.orderNo] || { isLocked: false, status: {} };
                      const isLocked = lockStatus.isLocked;
                      // In Measurements tab, we allow editing even after confirmation, so isConfirmed is not used to disable buttons
                      const isConfirmed = lockStatus.status?.confirmed === true;
                      const orderPaymentProof = order.paymentProof || order.payment_proof;
                      const orderPaymentProofUploadedAt = order.paymentProofUploadedAt || order.payment_proof_uploaded_at;
                        
                        // Calculate pricing for admin order
                        const adminPricing = calculateOrderPricing(order, order.orderNo);
                        
                        // Lock status fetching is handled by useEffect to avoid render loop

                      return (
                        <div key={`order-${orderId}-${idx}`} className={`measurement-order-card ${isExpanded ? 'expanded' : ''} ${allSubmitted ? 'all-submitted' : ''}`}>
                          <div
                            className={`order-card-header measurement-order-header-clickable ${allSubmitted ? 'all-submitted-header' : ''}`}
                            onClick={() => {
                              setExpandedMeasurementOrders(prev => {
                                const newSet = new Set(prev);
                                if (newSet.has(orderId)) {
                                  newSet.delete(orderId);
                                } else {
                                  newSet.add(orderId);
                                }
                                return newSet;
                              });
                            }}
                          >
                            <div>
                              <h3>Order #{order.orderNo || 'N/A'}</h3>
                              <p className="order-date">
                                Created: {order.orderDate
                                  ? formatDate(order.orderDate)
                                  : "N/A"}
                              </p>
                              {allSubmitted && (
                                <div className="all-submitted-message">
                                  🎉 All recipients have submitted measurements! ✅
                                  <br />
                                  <strong>Your order is ready for processing.</strong>
                                </div>
                              )}
                            </div>
                            <div className="order-header-right">
                              <div className="order-stats">
                                <span className="stat-item">
                                  Total: {order.totalQuantity || 0} outfits
                                </span>
                                <span className="stat-item" style={{ fontWeight: 'bold', color: '#b5651d', marginLeft: '15px' }}>
                                  Price: {formatCurrency(adminPricing.total, adminPricing.currency.code)} {adminPricing.currency.code}
                                </span>
                              </div>
                              <div className="order-toggle-icon">
                                <span className={`toggle-arrow ${isExpanded ? 'expanded' : ''}`}>
                                  ▼
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className={`measurement-order-drawer ${isExpanded ? 'open' : 'closed'}`}>
                            <div className="order-admin-info">
                              <p><strong>Admin:</strong> {order.adminName || 'N/A'} {order.adminEmail ? `(${order.adminEmail})` : ''}</p>
                            </div>

                            <div className="order-looks">
                              {order.looks && order.looks.map((look, lookIdx) => (
                                <div key={look.lookId || lookIdx} className="look-section">
                                  <div className="look-header">
                                    <h4>{look.lookName}</h4>
                                    <span className="look-quantity">Qty: {look.quantity}</span>
                                  </div>

                                  {look.image && (
                                    <div className="look-image">
                                      <img src={look.image} alt={look.lookName} />
                                    </div>
                                  )}

                                  <div className="look-specs">
                                    <p><strong>Kurta:</strong> {look.kurtaColor} • {look.kurtaFabric}</p>
                                    <p><strong>Pajama:</strong> {look.pajamaColor} • {look.pajamaFabric}</p>
                                    <p><strong>Shawl:</strong> {look.shawl}</p>
                                  </div>

                                  <div className="recipients-status">
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                    <h5>Recipients Status:</h5>
                                        {!allSubmitted && (
                                          <button
                                            className="add-recipient-btn"
                                            onClick={() => setAddingRecipient({ orderNo: order.orderNo, lookId: look.lookId || look.id })}
                                            style={{
                                              padding: '8px 16px',
                                              background: '#28a745',
                                              color: 'white',
                                              border: 'none',
                                              borderRadius: '6px',
                                              cursor: 'pointer',
                                              fontSize: '14px',
                                              fontWeight: '600'
                                            }}
                                          >
                                            + Add Recipient
                                          </button>
                                        )}
                                      </div>
                                      
                                      {/* Add Recipient Form */}
                                      {addingRecipient && addingRecipient.orderNo === order.orderNo && addingRecipient.lookId === (look.lookId || look.id) && (
                                        <div className="add-recipient-form" style={{
                                          background: '#f8f9fa',
                                          padding: '15px',
                                          borderRadius: '8px',
                                          marginBottom: '15px',
                                          border: '2px solid #28a745'
                                        }}>
                                          <h6 style={{ margin: '0 0 10px 0', color: '#28a745' }}>Add New Recipient</h6>
                                          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                                            <div style={{ flex: '1', minWidth: '150px' }}>
                                              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '600' }}>Name *</label>
                                              <input
                                                type="text"
                                                value={newRecipient.name}
                                                onChange={(e) => setNewRecipient({ ...newRecipient, name: e.target.value })}
                                                placeholder="Full Name"
                                                style={{
                                                  width: '100%',
                                                  padding: '8px',
                                                  border: '1px solid #ddd',
                                                  borderRadius: '4px',
                                                  fontSize: '14px'
                                                }}
                                              />
                                            </div>
                                            <div style={{ flex: '1', minWidth: '200px' }}>
                                              <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: '600' }}>Email *</label>
                                              <input
                                                type="email"
                                                value={newRecipient.email}
                                                onChange={(e) => setNewRecipient({ ...newRecipient, email: e.target.value })}
                                                placeholder="email@example.com"
                                                style={{
                                                  width: '100%',
                                                  padding: '8px',
                                                  border: '1px solid #ddd',
                                                  borderRadius: '4px',
                                                  fontSize: '14px'
                                                }}
                                              />
                                            </div>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                              <button
                                                onClick={() => handleAddRecipient(order.orderNo, look.lookId || look.id)}
                                                disabled={isLocked || updatingRecipients[`${order.orderNo}-${look.lookId || look.id}-add`]}
                                                style={{
                                                  padding: '8px 16px',
                                                  background: isLocked ? '#6c757d' : '#28a745',
                                                  color: 'white',
                                                  border: 'none',
                                                  borderRadius: '4px',
                                                  cursor: isLocked ? 'not-allowed' : 'pointer',
                                                  fontSize: '14px',
                                                  fontWeight: '600',
                                                  opacity: isLocked ? 0.6 : 1
                                                }}
                                              >
                                                {updatingRecipients[`${order.orderNo}-${look.lookId || look.id}-add`] ? 'Adding...' : 'Add'}
                                              </button>
                                              <button
                                                onClick={() => {
                                                  setAddingRecipient(null);
                                                  setNewRecipient({ name: "", email: "" });
                                                }}
                                                style={{
                                                  padding: '8px 16px',
                                                  background: '#6c757d',
                                                  color: 'white',
                                                  border: 'none',
                                                  borderRadius: '4px',
                                                  cursor: 'pointer',
                                                  fontSize: '14px',
                                                  fontWeight: '600'
                                                }}
                                              >
                                                Cancel
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      )}

                                    <div className="recipients-list">
                                        {look.recipients && look.recipients.map((recipient, recIdx) => {
                                          const isEditingEmail = editingRecipient && 
                                            editingRecipient.orderNo === order.orderNo && 
                                            editingRecipient.lookId === (look.lookId || look.id) &&
                                            editingRecipient.recipientIndex === recIdx &&
                                            editingRecipient.field === 'email';
                                          
                                          const isEditingName = editingRecipient && 
                                            editingRecipient.orderNo === order.orderNo && 
                                            editingRecipient.lookId === (look.lookId || look.id) &&
                                            editingRecipient.recipientIndex === recIdx &&
                                            editingRecipient.field === 'name';

                                          const updateKey = `${order.orderNo}-${look.lookId || look.id}-${recIdx}`;
                                          
                                          return (
                                        <div
                                          key={recipient.recipientId || recIdx}
                                          className={`recipient-status-item ${recipient.status === 'SUBMITTED' ? 'submitted' : 'pending'}`}
                                              style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                padding: '16px',
                                                marginBottom: '12px',
                                                background: recipient.status === 'SUBMITTED' ? '#d4edda' : '#fffbf7',
                                                borderRadius: '12px',
                                                border: `2px solid ${recipient.status === 'SUBMITTED' ? '#28a745' : '#b5651d'}`,
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                transition: 'all 0.2s ease'
                                              }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-2px)';
                                                e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                                              }}
                                            >
                                              <div className="recipient-info" style={{  display: 'flex', flexDirection: 'column', gap: '12px', minWidth: 0, marginBottom: '12px' }}>
                                                {isEditingName ? (
                                                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
                                                    <input
                                                      type="text"
                                                      value={editingValue}
                                                      onChange={(e) => setEditingValue(e.target.value)}
                                                      onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                          handleUpdateRecipient(order.orderNo, look.lookId || look.id, recIdx, 'name', editingValue);
                                                        } else if (e.key === 'Escape') {
                                                          setEditingRecipient(null);
                                                          setEditingValue("");
                                                        }
                                                      }}
                                                      autoFocus
                                                      style={{
                                                        padding: '8px 12px',
                                                        border: '2px solid #b5651d',
                                                        borderRadius: '8px',
                                                        fontSize: '14px',
                                                        flex: '1',
                                                        minWidth: '150px',
                                                        outline: 'none',
                                                        transition: 'border-color 0.2s'
                                                      }}
                                                    />
                                                    <button
                                                      onClick={() => handleUpdateRecipient(order.orderNo, look.lookId || look.id, recIdx, 'name', editingValue)}
                                                      disabled={isLocked || updatingRecipients[updateKey]}
                                                      style={{
                                                        padding: '8px 16px',
                                                        background: isLocked ? '#6c757d' : '#b5651d',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        cursor: isLocked ? 'not-allowed' : 'pointer',
                                                        fontSize: '13px',
                                                        fontWeight: '600',
                                                        opacity: isLocked ? 0.6 : 1,
                                                        transition: 'all 0.2s ease'
                                                      }}
                                                    >
                                                      {updatingRecipients[updateKey] ? 'Saving...' : 'Save'}
                                                    </button>
                                                    <button
                                                      onClick={() => {
                                                        setEditingRecipient(null);
                                                        setEditingValue("");
                                                      }}
                                                      style={{
                                                        padding: '8px 16px',
                                                        background: '#6c757d',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer',
                                                        fontSize: '13px',
                                                        fontWeight: '600',
                                                        transition: 'all 0.2s ease'
                                                      }}
                                                    >
                                                      Cancel
                                                    </button>
                                          </div>
                                                ) : (
                                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                    <span style={{ fontSize: '18px', flexShrink: 0, color: '#6c5ce7' }}>👤</span>
                                                    <span className="recipient-name" style={{ 
                                                      cursor: (!isLocked && !allSubmitted && recipient.status !== 'SUBMITTED') ? 'pointer' : 'default',
                                                      fontSize: '15px',
                                                      fontWeight: '700',
                                                      color: '#2c3e50',
                                                      flex: '1',
                                                      minWidth: 0,
                                                      wordBreak: 'break-word'
                                                    }}>
                                                      {recipient.name}
                                                    </span>
                                                    {!isLocked && !allSubmitted && (
                                                      <button
                                                        onClick={() => {
                                                          setEditingRecipient({ orderNo: order.orderNo, lookId: look.lookId || look.id, recipientIndex: recIdx, field: 'name' });
                                                          setEditingValue(recipient.name);
                                                        }}
                                                        disabled={isLocked}
                                                        style={{
                                                          padding: '4px 10px',
                                                          background: '#fffbf7',
                                                          border: '1px solid #b5651d',
                                                          borderRadius: '6px',
                                                          color: '#b5651d',
                                                          cursor: 'pointer',
                                                          fontSize: '12px',
                                                          fontWeight: '500',
                                                          transition: 'all 0.2s ease',
                                                          whiteSpace: 'nowrap',
                                                          boxShadow: '0 1px 2px rgba(181, 101, 29, 0.1)'
                                                        }}
                                                        title="Edit name"
                                                        onMouseEnter={(e) => {
                                                          e.target.style.background = '#b5651d';
                                                          e.target.style.color = 'white';
                                                          e.target.style.transform = 'scale(1.05)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                          e.target.style.background = '#fffbf7';
                                                          e.target.style.color = '#b5651d';
                                                          e.target.style.transform = 'scale(1)';
                                                        }}
                                                      >
                                                        ✏️ Edit
                                                      </button>
                                                    )}
                                                  </div>
                                                )}
                                                
                                                {isEditingEmail ? (
                                                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '5px' }}>
                                                    <input
                                                      type="email"
                                                      value={editingValue}
                                                      onChange={(e) => setEditingValue(e.target.value)}
                                                      onKeyDown={(e) => {
                                                        if (e.key === 'Enter') {
                                                          handleUpdateRecipient(order.orderNo, look.lookId || look.id, recIdx, 'email', editingValue);
                                                        } else if (e.key === 'Escape') {
                                                          setEditingRecipient(null);
                                                          setEditingValue("");
                                                        }
                                                      }}
                                                      autoFocus
                                                      style={{
                                                        padding: '8px 12px',
                                                        border: '2px solid #b5651d',
                                                        borderRadius: '8px',
                                                        fontSize: '14px',
                                                        flex: '1',
                                                        minWidth: '200px',
                                                        outline: 'none',
                                                        transition: 'border-color 0.2s'
                                                      }}
                                                    />
                                                    <button
                                                      onClick={() => handleUpdateRecipient(order.orderNo, look.lookId || look.id, recIdx, 'email', editingValue)}
                                                      disabled={isLocked || updatingRecipients[updateKey]}
                                                      style={{
                                                        padding: '8px 16px',
                                                        background: isLocked ? '#6c757d' : '#b5651d',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        cursor: isLocked ? 'not-allowed' : 'pointer',
                                                        fontSize: '13px',
                                                        fontWeight: '600',
                                                        opacity: isLocked ? 0.6 : 1,
                                                        transition: 'all 0.2s ease'
                                                      }}
                                                    >
                                                      {updatingRecipients[updateKey] ? 'Saving...' : 'Save'}
                                                    </button>
                                                    <button
                                                      onClick={() => {
                                                        setEditingRecipient(null);
                                                        setEditingValue("");
                                                      }}
                                                      style={{
                                                        padding: '8px 16px',
                                                        background: '#6c757d',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer',
                                                        fontSize: '13px',
                                                        fontWeight: '600',
                                                        transition: 'all 0.2s ease'
                                                      }}
                                                    >
                                                      Cancel
                                                    </button>
                                                  </div>
                                                ) : (
                                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                                    <span style={{ fontSize: '18px', flexShrink: 0, color: '#6c5ce7' }}>📧</span>
                                                    <span className="recipient-email" style={{ 
                                                      cursor: (!isLocked && !allSubmitted && recipient.status !== 'SUBMITTED') ? 'pointer' : 'default',
                                                      fontSize: '14px',
                                                      color: '#2c3e50',
                                                      flex: '1',
                                                      minWidth: 0,
                                                      wordBreak: 'break-word'
                                                    }}>
                                                      {recipient.email}
                                                    </span>
                                                    {!isLocked && !allSubmitted && (
                                                      <button
                                                        onClick={() => {
                                                          setEditingRecipient({ orderNo: order.orderNo, lookId: look.lookId || look.id, recipientIndex: recIdx, field: 'email' });
                                                          setEditingValue(recipient.email);
                                                        }}
                                                        disabled={isLocked}
                                                        style={{
                                                          padding: '4px 10px',
                                                          background: '#fffbf7',
                                                          border: '1px solid #b5651d',
                                                          borderRadius: '6px',
                                                          color: '#b5651d',
                                                          cursor: 'pointer',
                                                          fontSize: '12px',
                                                          fontWeight: '500',
                                                          transition: 'all 0.2s ease',
                                                          whiteSpace: 'nowrap',
                                                          boxShadow: '0 1px 2px rgba(181, 101, 29, 0.1)'
                                                        }}
                                                        title="Edit email"
                                                        onMouseEnter={(e) => {
                                                          e.target.style.background = '#b5651d';
                                                          e.target.style.color = 'white';
                                                          e.target.style.transform = 'scale(1.05)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                          e.target.style.background = '#fffbf7';
                                                          e.target.style.color = '#b5651d';
                                                          e.target.style.transform = 'scale(1)';
                                                        }}
                                                      >
                                                        ✏️ Edit
                                                      </button>
                                                    )}
                                                  </div>
                                                )}
                                              </div>
                                              
                                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', marginTop: '8px', paddingTop: '12px', borderTop: '1px solid rgba(0,0,0,0.1)' }}>
                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                  {!allSubmitted && recipient.status === "PENDING" && (
                                                    <button
                                                      className="send-reminder"
                                                      disabled={sendingMap[recipient.email]}
                                                      onClick={() => sendReminder(order.orderNo, recipient.email)}
                                                      style={{
                                                        padding: '8px 16px',
                                                        background: '#ffc107',
                                                        color: '#000',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        cursor: 'pointer',
                                                        fontSize: '13px',
                                                        fontWeight: '600',
                                                        whiteSpace: 'nowrap'
                                                      }}
                                                    >
                                                      {sendingMap[recipient.email] ? "Sending..." : "🔔 Reminder"}
                                                    </button>
                                                  )}

                                                  {!allSubmitted && (
                                                    <button
                                                      onClick={() => handleDeleteRecipient(order.orderNo, look.lookId || look.id, recIdx)}
                                                      disabled={isLocked || updatingRecipients[updateKey]}
                                                      style={{
                                                        padding: '8px 16px',
                                                        background: isLocked ? '#6c757d' : '#dc3545',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        cursor: isLocked ? 'not-allowed' : 'pointer',
                                                        fontSize: '13px',
                                                        fontWeight: '600',
                                                        opacity: isLocked ? 0.6 : 1,
                                                        transition: 'all 0.2s ease',
                                                        boxShadow: isLocked ? 'none' : '0 2px 4px rgba(220, 53, 69, 0.2)',
                                                        whiteSpace: 'nowrap',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px'
                                                      }}
                                                      title={isLocked ? "Order locked - cannot remove" : "Remove recipient"}
                                                      onMouseEnter={(e) => {
                                                        if (!isLocked) {
                                                          e.target.style.background = '#c82333';
                                                          e.target.style.transform = 'translateY(-1px)';
                                                        }
                                                      }}
                                                      onMouseLeave={(e) => {
                                                        if (!isLocked) {
                                                          e.target.style.background = '#dc3545';
                                                          e.target.style.transform = 'translateY(0)';
                                                        }
                                                      }}
                                                    >
                                                      🗑️ Remove
                                                    </button>
                                                  )}
                                                </div>

                                                <div className="status-badge-wrapper" style={{ flexShrink: 0, marginLeft: 'auto' }}>
                                            {recipient.status === 'SUBMITTED' ? (
                                                    <div className="status-badge submitted-badge" style={{
                                                      padding: '10px 16px',
                                                      background: '#28a745',
                                                      color: 'white',
                                                      borderRadius: '8px',
                                                      fontSize: '13px',
                                                      fontWeight: '700',
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      alignItems: 'center',
                                                      gap: '4px',
                                                      boxShadow: '0 2px 4px rgba(40, 167, 69, 0.3)',
                                                      minWidth: '120px'
                                                    }}>
                                                      <span>✓ SUBMITTED</span>
                                                {recipient.submittedAt && (
                                                        <span className="submission-date" style={{ fontSize: '11px', opacity: 0.95, fontWeight: '500' }}>
                                                    {formatDate(recipient.submittedAt)}
                                                  </span>
                                                )}
                                                    </div>
                                                  ) : (
                                                    <span className="status-badge pending-badge" style={{
                                                      padding: '6px 14px',
                                                      background: '#b5651d',
                                                      color: 'white',
                                                      borderRadius: '8px',
                                                      fontSize: '12px',
                                                      fontWeight: '600',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: '6px',
                                                      boxShadow: '0 2px 4px rgba(181, 101, 29, 0.3)'
                                                    }}>
                                                ⏳ Pending
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                            </div>
                                          );
                                        })}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* User Submissions Section */}
            <div className="submissions-section">
              <h2>My Measurement Submissions</h2>

              {submissions.length === 0 ? (
                <div className="no-submissions">
                  <p>You haven't submitted any measurements yet.</p>
                  <button
                    onClick={() => navigate("/customization")}
                    className="btn-primary"
                  >
                    Create Custom Look
                  </button>
                </div>
              ) : (
                <div className="submissions-grid">
                  {submissions.map((submission, index) => (
                    <div key={submission.id || index} className="submission-card">
                      <div className="card-header">
                        <h3>Order #{submission.order_no}</h3>
                        <span className={`status-badge ${submission.status?.toLowerCase()}`}>
                          {submission.status || "SUBMITTED"}
                        </span>
                      </div>

                      <div className="card-content">
                        <div className="product-details">
                          <h4>Product Details</h4>
                          <div className="detail-row">
                            <span className="label">Look Name:</span>
                            <span className="value"><b> {submission.look_name || "N/A"}</b></span>
                          </div>
                          <div className="detail-row">
                            <span className="label">Look Title:</span>
                            <span className="value"><b> {submission.look_title || "N/A"}</b></span>
                          </div>
                          {submission.image && (
                            <div className="product-image">
                              <img src={submission.image} alt={submission.look_title} />
                            </div>
                          )}
                          <div className="specs-grid">
                            <div className="spec-item">
                              <span className="spec-label">Kurta Color:</span>
                              <span className="spec-value">{submission.kurta_color || "N/A"}</span>
                            </div>
                            <div className="spec-item">
                              <span className="spec-label">Kurta Fabric:</span>
                              <span className="spec-value">{submission.kurta_fabric || "N/A"}</span>
                            </div>
                            <div className="spec-item">
                              <span className="spec-label">Pajama Color:</span>
                              <span className="spec-value">{submission.pajama_color || "N/A"}</span>
                            </div>
                            <div className="spec-item">
                              <span className="spec-label">Pajama Fabric:</span>
                              <span className="spec-value">{submission.pajama_fabric || "N/A"}</span>
                            </div>
                            <div className="spec-item">
                              <span className="spec-label">Shawl:</span>
                              <span className="spec-value">{submission.shawl || "N/A"}</span>
                            </div>
                            <div className="spec-item">
                              <span className="spec-label">Quantity:</span>
                              <span className="spec-value">{submission.quantity || "N/A"}</span>
                            </div>
                          </div>
                        </div>

                        <div className="measurements-section">
                          <h4>Your Measurements</h4>
                          {submission.measurements && Array.isArray(submission.measurements) ? (
                            <div className="measurements-grid">
                              {submission.measurements.map((measurement, idx) => (
                                <div key={idx} className="measurement-item">
                                  <span className="measurement-label">{measurement.label}:</span>
                                  <span className="measurement-value">{measurement.value} inches</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p>No measurements data available</p>
                          )}
                        </div>

                        <div className="submission-meta">
                          <div className="meta-item">
                            <span className="meta-label">Submitted:</span>
                            <span className="meta-value">
                              {submission.submitted_at ? formatDate(submission.submitted_at) : "N/A"}
                            </span>
                          </div>
                          <div className="meta-item">
                            <span className="meta-label">Admin:</span>
                            <span className="meta-value">{submission.admin_name || submission.admin_email || "N/A"}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Order Confirmation Popup */}
      {showConfirmPopup && (
        <div className="order-confirmation-popup-overlay" onClick={() => setShowConfirmPopup(false)}>
          <div className="order-confirmation-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-content">
              <div className="popup-icon">✅</div>
              <h2>Thank You for Confirming Order!</h2>
              <p>Our team will check your order details and confirm your order via email.</p>
              <button 
                className="popup-close-btn"
                onClick={() => setShowConfirmPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shawl Payment Proof Review Popup */}
      {showShawlPaymentReviewPopup && (
        <div className="order-confirmation-popup-overlay" onClick={() => setShowShawlPaymentReviewPopup(false)}>
          <div className="order-confirmation-popup" onClick={(e) => e.stopPropagation()}>
            <div className="popup-content">
              <div className="popup-icon">⌛</div>
              <h2>Payment Proof Received</h2>
              <p>
                Our team is reviewing your order and payment. Once the payment is verified,
                we will send you an order confirmation email.
              </p>
              <button 
                className="popup-close-btn"
                onClick={() => setShowShawlPaymentReviewPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default Profile;

